import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api, { fmt, fmtQty, fmtDate } from '../../utils/api';
import { aggregateByModel } from '../../utils/modelKey';
import { notifyError, notifyMessage } from '../../utils/notify';
import { confirm } from '../../utils/confirm';
import * as XLSX from 'xlsx';
import CellcomPage from '../Cellcom/CellcomPage';
import { QRSerialsView, ExternalWarehouseView, PullOrdersView } from '../Logistics/LogisticsPage';

const WAREHOUSE_TABS = ['allocations', 'newOrder', 'futureOrder', 'cellcomUpload', 'extWarehouse', 'pullOrders', 'qrSerials'];
const CELLCOM_TABS = ['cellcomOrders', 'completed', 'allOrders'];
const ALL_TABS = [...WAREHOUSE_TABS, ...CELLCOM_TABS, 'archived'];
const PALLET_CBM = 1.0 * 1.2 * 2.05; // 2.46 CBM per pallet (1.0×1.2m, h=2.05m usable)
const PALLETS_PER_40HC = 20; // 40HC floor: 20 pallets max
const fmtWeight = (kg) => kg >= 1000 ? `${(kg / 1000).toFixed(2)}t` : `${kg.toFixed(1)} kg`;
const ZONE_COLORS = {
  green:  { bg: '#e8f5e9', border: '#4caf50', label: '🟢 Available Now', text: '#2e7d32' },
  yellow: { bg: '#fffde7', border: '#fbc02d', label: '🟡 Arriving ≤30 days', text: '#f57f17' },
  orange: { bg: '#fff3e0', border: '#ff9800', label: '🟠 Arriving 30-60 days', text: '#e65100' },
  red:    { bg: '#fbe9e7', border: '#f44336', label: '🔴 60+ days', text: '#c62828' },
};

// Master carton validation — shared by CellcomOrderDetail and WarehousePage
// L5 fix 2026-05-27: now async — returns Promise<number> (upper or lower rounded).
const validateMasterCartonFn = async (qty, mc, he) => {
  if (!qty || qty <= 0 || mc <= 1) return qty;
  if (qty % mc === 0) return qty;
  const lower = Math.floor(qty / mc) * mc;
  const upper = Math.ceil(qty / mc) * mc;
  const msg = he
    ? `${qty} לא כפולה של ${mc} (master carton).`
    : `${qty} is not a multiple of ${mc} (master carton).`;
  const ok = await confirm({
    title: he ? 'עיגול לקרטון מלא' : 'Round to master carton',
    body: msg,
    confirmLabel: String(upper),
    cancelLabel: String(lower),
  });
  return ok ? upper : lower;
};

function CellcomOrderDetail({ orderNumber, onItemsChanged, actionButtons, allOrders }) {
  const { t, i18n } = useTranslation();
  const he = i18n.language === 'he';
  const [items, setItems] = useState([]);
  const [orderData, setOrderData] = useState({});
  const [priceEdits, setPriceEdits] = useState({});
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showPricing, setShowPricing] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  const [nisRate, setNisRate] = useState(''); // $→₪ rate for the C/M purchase orders
  const [dnForm, setDnForm] = useState(null);
  const [dnSel, setDnSel] = useState(null);      // {noteNumber, transportMode, shipDate} for the selected-lines panel
  const [dnQty, setDnQty] = useState({});        // itemId -> units to put on the note
  const [dnOpen, setDnOpen] = useState({});      // itemId -> units still unassigned to any note
  const [dnCover, setDnCover] = useState({});    // itemId -> {assigned, remaining} for the order table column   // {noteNumber, transportMode, shipDate} while the whole-order dialog is open
  const [dnBusy, setDnBusy] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [lastMove, setLastMove] = useState(null); // for undo
  const [itemSort, setItemSort] = useState({ field: null, dir: 'asc' });
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [addStock, setAddStock] = useState([]);
  const [addFutureSI, setAddFutureSI] = useState([]);
  const [addInputs, setAddInputs] = useState({});
  const [addSearch, setAddSearch] = useState('');
  const [addExpanded, setAddExpanded] = useState(new Set());
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'warehouse');

  // How much of each line already sits on a delivery note. Failures are swallowed so
  // the order screen still renders if the endpoint is unavailable — the column just
  // shows a dash rather than the page breaking.
  const loadDnCover = () => api.get(`/delivery-notes/order/${orderNumber}/open`)
    .then(r => {
      const m = {};
      (r.data.items || []).forEach(i => {
        m[i.item_id] = { assigned: parseFloat(i.assigned) || 0, remaining: parseFloat(i.remaining) || 0 };
      });
      setDnCover(m);
    })
    .catch(() => setDnCover({}));

  useEffect(() => {
    api.get(`/warehouse/cellcom-orders/${orderNumber}`).then(res => { setItems(res.data.items || []); setOrderData(res.data); }).catch(() => {});
    loadDnCover();
  }, [orderNumber]);
  const reload = () => {
    loadDnCover();
    return api.get(`/warehouse/cellcom-orders/${orderNumber}`).then(res => { setItems(res.data.items || []); setOrderData(res.data); }).catch(() => {});
  };

  const savePrice = async (itemId, value) => {
    const price = parseFloat(value);
    if (isNaN(price)) return;
    try {
      await api.put(`/warehouse/cellcom-order-item/${itemId}/price`, { sellingPrice: price });
      setItems(prev => prev.map(it => it.id === itemId ? { ...it, selling_price: price } : it));
      setPriceEdits(prev => { const n = { ...prev }; delete n[itemId]; return n; });
      if (showPricing) await refreshPricing(); // keep pricing columns + profit band in sync with price change
    } catch {}
  };

  const removeSelected = async () => {
    if (selectedItems.size === 0) return;
    if (!(await confirm({
      body: he ? `להסיר ${selectedItems.size} פריט(ים) מהזמנה #${orderNumber}?` : `Remove ${selectedItems.size} item(s) from order #${orderNumber}?`,
      danger: true,
    }))) return;
    try {
      await api.put('/warehouse/cellcom-order-items/remove', { itemIds: Array.from(selectedItems) });
      setSelectedItems(new Set());
      reload();
      if (onItemsChanged) onItemsChanged();
    } catch (err) { notifyError(err); }
  };

  const toggleItem = (id) => setSelectedItems(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () => setSelectedItems(prev => prev.size === items.length ? new Set() : new Set(items.map(i => i.id)));

  const fetchPricing = async () => {
    try {
      const res = await api.get('/profitability/cellcom-orders');
      const orders = Array.isArray(res.data) ? res.data : (res.data.orders || []);
      return orders.find(o => String(o.orderNumber) === String(orderNumber)) || null;
    } catch { return null; }
  };

  const togglePricing = async () => {
    if (showPricing) { setShowPricing(false); return; }
    if (!pricingData) { const order = await fetchPricing(); if (order) setPricingData(order); }
    setShowPricing(true);
  };

  // Re-fetch pricing after an edit (M%, M expenses, air rate) — keeps the panel open with fresh numbers
  const refreshPricing = async () => {
    setPricingData(await fetchPricing());
    setShowPricing(true);
  };

  // One-click purchase order (הזמנת רכש) in ₪ for the C or M layer. Prices converted from $ via nisRate.
  const generatePO = (layer) => {
    const rate = parseFloat(nisRate);
    if (!rate || rate <= 0) { alert(he ? 'הזן שער ₪/$ תקין (לדוגמה 3.7)' : 'Enter a valid ₪/$ rate (e.g. 3.7)'); return; }
    const its = pricingData?.items || [];
    if (!its.length) return;
    const d = new Date();
    const dateStr = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    const priceOf = (pi) => layer === 'M' ? pi.mPrice : pi.cPrice;
    const r2 = (n) => Math.round(n * 100) / 100;
    const aoa = [];
    aoa.push([`הזמנת רכש – ${layer}`]);
    aoa.push(['מספר הזמנת רכש:', `${orderNumber}-${layer}`, '', 'תאריך:', dateStr]);
    aoa.push(['הזמנה מקור:', String(orderNumber), '', 'שער ₪/$:', rate]);
    aoa.push([]);
    aoa.push(['מק"ט', 'מוצר', 'כמות', 'מחיר יחידה (₪)', 'סה"כ (₪)']);
    let grand = 0;
    for (const pi of its) {
      const unitNis = r2(priceOf(pi) * rate);
      const lineNis = r2(unitNis * pi.quantity);
      grand += lineNis;
      aoa.push([pi.sku, pi.description, pi.quantity, unitNis, lineNis]);
    }
    aoa.push([]);
    aoa.push(['', '', '', 'סה"כ (₪):', r2(grand)]);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 14 }, { wch: 32 }, { wch: 8 }, { wch: 15 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    wb.Workbook = { Views: [{ RTL: true }] };
    XLSX.utils.book_append_sheet(wb, ws, `PO ${layer}`);
    XLSX.writeFile(wb, `PO_${layer}_${orderNumber}.xlsx`);
  };

  if (!items.length) return <div className="text-muted" style={{ padding: 12 }}>Loading...</div>;
  return (
    <div>
      {/* Action buttons + Remove on same line */}
      <div style={{ display: 'flex', gap: 8, padding: '8px 12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {canEdit && selectedItems.size > 0 && (
          <>
            <button className="btn btn-sm" style={{ background: '#c62828', color: '#fff' }} onClick={removeSelected}>
              Remove {selectedItems.size} item(s)
            </button>
            <button className="btn btn-sm" style={{ background: '#0f766e', color: '#fff' }}
              onClick={async () => {
                // Pull the unassigned remainder so each selected line defaults to what
                // is genuinely still shippable, not its full original quantity.
                let openMap = {};
                try {
                  const r = await api.get(`/delivery-notes/order/${orderNumber}/open`);
                  (r.data.items || []).forEach(i => { openMap[i.item_id] = parseFloat(i.remaining); });
                } catch {
                  // Do not silently fall back to the full quantity — that would let the
                  // user build a note the server is certain to reject, line by line.
                  alert(he ? 'לא ניתן לטעון את היתרות מתעודות קיימות — נסה שוב' : 'Could not load remaining quantities — try again');
                  return;
                }
                const q = {};
                Array.from(selectedItems).forEach(id => {
                  const rem = openMap[id];
                  const it = items.find(x => x.id === id);
                  const dflt = rem !== undefined ? rem : (it ? parseFloat(it.quantity) : 0);
                  if (dflt > 0) q[id] = String(dflt);
                });
                setDnOpen(openMap);
                setDnQty(q);
                setDnSel({ noteNumber: '', transportMode: 'SEA', shipDate: (d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`)(new Date()) });
              }}>
              📦 {he ? `${selectedItems.size} שורות לתעודת משלוח` : `${selectedItems.size} → delivery note`}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <select className="input input-sm" value={moveTarget} onChange={async (e) => {
                const target = e.target.value;
                if (!target) { setMoveTarget(''); return; }
                setMoveTarget(target);
                // Ask qty for each selected item
                const moveItems = [];
                for (const id of selectedItems) {
                  const item = items.find(i => i.id === id);
                  if (!item) continue;
                  const maxQty = parseFloat(item.quantity);
                  const input = prompt(`${item.sku} — ${item.description}\n${he ? 'כמה להעביר?' : 'How many to move?'} (max: ${maxQty})\n\n${he ? 'הקלד מספר או ALL' : 'Enter a number or ALL'}`, 'ALL');
                  if (!input) continue;
                  const qty = input.toUpperCase() === 'ALL' ? maxQty : parseInt(input);
                  if (!qty || qty <= 0 || qty > maxQty) continue;
                  moveItems.push({ itemId: id, quantity: qty });
                }
                if (!moveItems.length) { setMoveTarget(''); return; }
                try {
                  const res = await api.put('/warehouse/cellcom-order-items/move', {
                    itemIds: moveItems.map(m => m.itemId),
                    targetOrderNumber: parseInt(target),
                    quantities: moveItems
                  });
                  setLastMove({ from: orderNumber, to: target, items: moveItems, undo: res.data?.undo });
                  setSelectedItems(new Set());
                  setMoveTarget('');
                  reload();
                  if (onItemsChanged) onItemsChanged();
                } catch (err) { notifyError(err); }
              }} style={{ width: 140, fontSize: 12 }}>
                <option value="">{he ? 'העבר ל...' : 'Move to...'}</option>
                {(allOrders || []).filter(o => String(o) !== String(orderNumber)).map(o => (
                  <option key={o} value={o}>#{o}</option>
                ))}
              </select>
            </div>
          </>
        )}
        {lastMove && (
          <button className="btn btn-sm" style={{ background: '#f59e0b', color: '#fff', border: 'none' }} onClick={async () => {
            if (!(await confirm({ body: he ? `לבטל העברה ל-#${lastMove.to}?` : `Undo move to #${lastMove.to}?` }))) return;
            try {
              // Reverse: move items back from target to source
              await api.put('/warehouse/cellcom-order-items/move', {
                itemIds: lastMove.items.map(m => m.itemId),
                targetOrderNumber: parseInt(lastMove.from),
                quantities: lastMove.items
              });
              setLastMove(null);
              reload();
              if (onItemsChanged) onItemsChanged();
            } catch (err) { notifyError(err); }
          }}>
            ↩ {he ? 'בטל העברה' : 'Undo Move'}
          </button>
        )}
        <button className={`btn btn-sm ${showPricing ? 'btn-gold' : 'btn-outline'}`} onClick={togglePricing}>
          {showPricing ? '✕ Hide Pricing' : '💰 Show Pricing'}
        </button>
        {showPricing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f0f7ff', padding: '4px 10px', borderRadius: 6, fontSize: 12 }}>
            <span style={{ fontWeight: 600, color: '#1565c0' }}>M %:</span>
            <input className="inline-edit" type="number" step="0.25" min="0" max="10" style={{ width: 55, textAlign: 'right', fontSize: 12 }}
              defaultValue={pricingData?.mPercentage ?? 1.75}
              onBlur={async (e) => {
                const raw = parseFloat(e.target.value);
                const val = Number.isFinite(raw) && raw >= 0 ? raw : 1.75;
                if (val !== (pricingData?.mPercentage ?? 1.75)) {
                  await api.put('/profitability/cellcom-item', { orderNumber, mPercentage: val });
                  await refreshPricing();
                }
              }}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
            />
            <span style={{ color: '#64748b' }}>%</span>
          </div>
        )}
        {showPricing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface2)', padding: '4px 10px', borderRadius: 6, fontSize: 12 }}>
            <span style={{ fontWeight: 600, color: (orderData.transport_mode || '').toUpperCase() === 'AIR' ? '#f97316' : '#7b1fa2' }}>
              {(orderData.transport_mode || '').toUpperCase() === 'AIR' ? '✈️' : '🚢'} M Expenses:
            </span>
            <span style={{ fontFamily: "'DM Mono', monospace" }}>$</span>
            <input className="inline-edit" type="number" step="100" style={{ width: 90, textAlign: 'right', fontSize: 12 }}
              defaultValue={pricingData?.mExpenses || 0}
              onBlur={async (e) => {
                const val = parseFloat(e.target.value) || 0;
                if (val !== (pricingData?.mExpenses || 0)) {
                  await api.put('/profitability/cellcom-item', { orderNumber, mExpenses: val });
                  await refreshPricing();
                }
              }}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
            />
            {(orderData.transport_mode || '').toUpperCase() === 'AIR' && (<>
              <span style={{ color: '#9ca3af', margin: '0 4px' }}>or</span>
              <span style={{ fontFamily: "'DM Mono', monospace" }}>$/kg</span>
              <input className="inline-edit" type="number" step="0.1" style={{ width: 60, textAlign: 'right', fontSize: 12 }}
                defaultValue={pricingData?.airRatePerKg || 0}
                onBlur={async (e) => {
                  const val = parseFloat(e.target.value) || 0;
                  if (val !== (pricingData?.airRatePerKg || 0)) {
                    await api.put('/profitability/cellcom-item', { orderNumber, airRatePerKg: val, mExpenses: 0 });
                    await refreshPricing();
                  }
                }}
                onKeyDown={e => e.key === 'Enter' && e.target.blur()}
              />
            </>)}
          </div>
        )}
        {showPricing && pricingData && (
          <button className="btn btn-sm btn-outline" style={{ color: '#2e7d32', borderColor: '#2e7d32' }} onClick={() => {
            const mLbl = `M Price (-${pricingData?.mPercentage ?? 1.75}%)`;
            const rows = (pricingData.items || []).map(pi => {
              const src = pi.sourceOrder === 'EXISTING-STOCK' || pi.sourceOrder === 'S&T' ? 'S&T' : pi.sourceOrder === 'SHAGER-STOCK' ? 'Shager' : pi.sourceOrder || '';
              return {
                'SKU': pi.sku,
                'Barcode': pi.barcode || '',
                'Product': pi.description,
                'Qty': pi.quantity,
                'Customer Price': pi.customerPrice,
                'M Expenses/Unit (incl. in C price)': pi.mExpensesPerUnit || 0,
                'M Expenses Total (incl. in C price)': pi.mExpensesTotal || 0,
                [mLbl]: pi.mPrice,
                'C Price (-1.5%)': pi.cPrice,
                'Shager Gets': pi.cPrice,
                'Purchase Loaded': pi.purchaseLoaded,
                'Profit/Unit': Math.round((pi.cPrice - pi.purchaseLoaded) * 100) / 100,
                'Total Profit': pi.profit,
                'Source': src,
                'Transport': pi.transportMode || '',
              };
            });
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, `Order ${orderNumber}`);
            XLSX.writeFile(wb, `pricing_order_${orderNumber}.xlsx`);
          }}>
            📥 Export Pricing
          </button>
        )}
        {showPricing && pricingData && (
          <button className="btn btn-sm btn-outline" style={{ color: '#1565c0', borderColor: '#1565c0' }} onClick={() => {
            const mLbl = `M Price (-${pricingData?.mPercentage ?? 1.75}%)`;
            const rows = (pricingData.items || []).map(pi => {
              const profitPerUnit = Math.round((pi.customerPrice - pi.mPrice) * 100) / 100;
              const shipPerUnit = pi.mExpensesPerUnit || 0;
              const netProfit = Math.round((profitPerUnit - shipPerUnit) * 100) / 100;
              return {
                'SKU': pi.sku,
                'Barcode': pi.barcode || '',
                'Product': pi.description,
                'Qty': pi.quantity,
                'Customer Price': pi.customerPrice,
                'M Expenses/Unit (incl. in C price)': shipPerUnit,
                'M Expenses Total (incl. in C price)': pi.mExpensesTotal || 0,
                [mLbl]: pi.mPrice,
                'profit per unit': profitPerUnit,
                'profit per. Unit minus shipping': shipPerUnit,
                'net profit': netProfit,
                'net profit total': Math.round(netProfit * pi.quantity * 100) / 100,
              };
            });
            if (rows.length) {
              const grand = Math.round(rows.reduce((s, r) => s + (r['net profit total'] || 0), 0) * 100) / 100;
              const blank = {}; Object.keys(rows[0]).forEach(k => { blank[k] = ''; });
              rows.push({ ...blank });
              rows.push({ ...blank, 'net profit total': grand });
            }
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, `Order ${orderNumber}`);
            XLSX.writeFile(wb, `m_profit_order_${orderNumber}.xlsx`);
          }}>
            📊 M Profit
          </button>
        )}
        {showPricing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', padding: '4px 10px', borderRadius: 6, fontSize: 12 }}>
            <span style={{ fontWeight: 600, color: '#15803d' }}>₪/$:</span>
            <input className="inline-edit" type="number" step="0.01" min="0" style={{ width: 60, textAlign: 'right', fontSize: 12 }}
              value={nisRate} onChange={e => setNisRate(e.target.value)} placeholder="3.70" />
          </div>
        )}
        {showPricing && pricingData && (
          <button className="btn btn-sm btn-outline" style={{ color: '#7b1fa2', borderColor: '#7b1fa2' }} onClick={() => generatePO('M')}>
            🧾 {he ? 'הזמנת רכש M' : 'PO – M'}
          </button>
        )}
        {showPricing && pricingData && (
          <button className="btn btn-sm btn-outline" style={{ color: '#1565c0', borderColor: '#1565c0' }} onClick={() => generatePO('C')}>
            🧾 {he ? 'הזמנת רכש C' : 'PO – C'}
          </button>
        )}
        {canEdit && (
          <button className="btn btn-sm btn-outline" style={{ color: '#0f766e', borderColor: '#0f766e' }}
            onClick={() => setDnForm({ noteNumber: '', transportMode: 'SEA', shipDate: (d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`)(new Date()) })}>
            📦 {he ? 'תעודת משלוח לכל ההזמנה' : 'Whole order → delivery note'}
          </button>
        )}
        {canEdit && (
          <button className={`btn btn-sm ${showAddProduct ? 'btn-gold' : ''}`} style={!showAddProduct ? { background: '#7c3aed', color: '#fff', border: 'none' } : {}}
            onClick={async () => {
              if (showAddProduct) { setShowAddProduct(false); return; }
              try {
                const [stockRes, logRes] = await Promise.all([api.get('/warehouse/stock-summary'), api.get('/logistics')]);
                setAddStock(stockRes.data || []);
                // Future SI = not received yet
                const allOrders2 = logRes.data.orders || logRes.data || [];
                setAddFutureSI(allOrders2.filter(o => o.doc_type === 'SI' && !o.received));
              } catch {}
              setShowAddProduct(true);
              setAddInputs({});
              setAddSearch('');
            }}>
            {showAddProduct ? '✕ Close' : '➕ Add Product'}
          </button>
        )}
        {actionButtons}
      </div>
      {/* Whole order -> one delivery note. Takes only the unassigned remainder, so
          pressing it after a partial note never ships the same units twice. */}
      {dnForm && canEdit && (
        <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, padding: 14, margin: '10px 0' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12 }}>{he ? 'מספר תעודה' : 'Note no.'} *<br />
              <input className="inline-edit" style={{ width: 140 }} value={dnForm.noteNumber}
                onChange={e => setDnForm({ ...dnForm, noteNumber: e.target.value })} placeholder="13538686" />
            </label>
            <label style={{ fontSize: 12 }}>{he ? 'הובלה' : 'Mode'} *<br />
              <select className="inline-edit" style={{ width: 110 }} value={dnForm.transportMode}
                onChange={e => setDnForm({ ...dnForm, transportMode: e.target.value })}>
                <option value="SEA">{he ? '\uD83D\uDEA2 ים' : 'Sea'}</option>
                <option value="AIR">{he ? '\u2708\uFE0F אוויר' : 'Air'}</option>
              </select>
            </label>
            <label style={{ fontSize: 12 }}>{he ? 'תאריך' : 'Date'}<br />
              <input className="inline-edit" type="date" style={{ width: 145 }} value={dnForm.shipDate}
                onChange={e => setDnForm({ ...dnForm, shipDate: e.target.value })} />
            </label>
            <button className="btn btn-sm btn-gold" disabled={dnBusy || !dnForm.noteNumber.trim()}
              onClick={async () => {
                setDnBusy(true);
                try {
                  const r = await api.post(`/delivery-notes/from-order/${orderNumber}`, dnForm);
                  alert(he
                    ? `נוצרה תעודה ${dnForm.noteNumber}: ${r.data.lineCount} שורות, ${r.data.units} יחידות`
                    : `Created note ${dnForm.noteNumber}: ${r.data.lineCount} lines, ${r.data.units} units`);
                  setDnForm(null);
                  loadDnCover();
                } catch (err) {
                  alert(err.response?.data?.error || (he ? 'שגיאה ביצירת תעודה' : 'Failed to create note'));
                }
                setDnBusy(false);
              }}>
              {dnBusy ? (he ? 'שומר…' : 'Saving…') : (he ? '\uD83D\uDCBE צור תעודה' : 'Create note')}
            </button>
            <button className="btn btn-sm btn-outline" onClick={() => setDnForm(null)}>
              {he ? 'ביטול' : 'Cancel'}
            </button>
            <span style={{ fontSize: 12, color: '#0f766e' }}>
              {he ? 'נכנסת רק היתרה שטרם שובצה לתעודה אחרת' : 'Only the unassigned remainder is taken'}
            </span>
          </div>
        </div>
      )}
      {/* Selected lines -> one delivery note. Quantity is per line, so half a line
          can ship now and the rest later. Capped at the unassigned remainder. */}
      {dnSel && canEdit && (() => {
        const rows = Array.from(selectedItems)
          .map(id => items.find(x => x.id === id)).filter(Boolean);
        const totalUnits = rows.reduce((sum, it) => sum + (parseFloat(dnQty[it.id]) || 0), 0);
        const bad = rows.filter(it => {
          const v = parseFloat(dnQty[it.id]);
          if (!(v > 0)) return false;
          const cap = dnOpen[it.id] !== undefined ? dnOpen[it.id] : parseFloat(it.quantity);
          return v > cap + 1e-9;
        });
        const chosen = rows.filter(it => parseFloat(dnQty[it.id]) > 0);
        return (
          <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, padding: 14, margin: '10px 0' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
              <label style={{ fontSize: 12 }}>{he ? 'מספר תעודה' : 'Note no.'} *<br />
                <input className="inline-edit" style={{ width: 140 }} value={dnSel.noteNumber}
                  onChange={e => setDnSel({ ...dnSel, noteNumber: e.target.value })} placeholder="13538686" />
              </label>
              <label style={{ fontSize: 12 }}>{he ? 'הובלה' : 'Mode'} *<br />
                <select className="inline-edit" style={{ width: 110 }} value={dnSel.transportMode}
                  onChange={e => setDnSel({ ...dnSel, transportMode: e.target.value })}>
                  <option value="SEA">{he ? '\uD83D\uDEA2 ים' : 'Sea'}</option>
                  <option value="AIR">{he ? '\u2708\uFE0F אוויר' : 'Air'}</option>
                </select>
              </label>
              <label style={{ fontSize: 12 }}>{he ? 'תאריך' : 'Date'}<br />
                <input className="inline-edit" type="date" style={{ width: 145 }} value={dnSel.shipDate}
                  onChange={e => setDnSel({ ...dnSel, shipDate: e.target.value })} />
              </label>
            </div>
            <div className="table-wrap" style={{ maxHeight: 260, overflow: 'auto', marginBottom: 10 }}>
              <table className="table" style={{ fontSize: 12 }}>
                <thead><tr>
                  <th>{he ? 'מוצר' : 'Product'}</th>
                  <th className="text-end">{he ? 'בהזמנה' : 'Ordered'}</th>
                  <th className="text-end">{he ? 'נותר לשיבוץ' : 'Unassigned'}</th>
                  <th className="text-end">{he ? 'לתעודה זו' : 'On this note'}</th>
                </tr></thead>
                <tbody>
                  {rows.map(it => {
                    const cap = dnOpen[it.id] !== undefined ? dnOpen[it.id] : parseFloat(it.quantity);
                    const v = dnQty[it.id] ?? '';
                    const over = v !== '' && parseFloat(v) > cap + 1e-9;
                    return (
                      <tr key={it.id}>
                        <td>{it.description}</td>
                        <td className="text-end">{fmtQty(it.quantity)}</td>
                        <td className="text-end" style={{ fontWeight: 600, color: cap > 0 ? undefined : '#94a3b8' }}>{fmtQty(cap)}</td>
                        <td className="text-end">
                          <input className="inline-edit" type="number" min="0" step="1"
                            style={{ width: 90, textAlign: 'right', borderColor: over ? '#dc2626' : undefined }}
                            value={v} onChange={e => setDnQty({ ...dnQty, [it.id]: e.target.value })} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {chosen.length} {he ? 'שורות' : 'lines'} · {fmtQty(totalUnits)} {he ? 'יח\u0027' : 'units'}
              </span>
              {bad.length > 0 && (
                <span style={{ color: '#b91c1c', fontSize: 12 }}>
                  {'\u26A0'} {bad.length} {he ? 'שורות חורגות מהיתרה' : 'lines exceed the remainder'}
                </span>
              )}
              <button className="btn btn-sm btn-gold" style={{ marginInlineStart: 'auto' }}
                disabled={dnBusy || !dnSel.noteNumber.trim() || !chosen.length || bad.length > 0}
                onClick={async () => {
                  setDnBusy(true);
                  try {
                    await api.post('/delivery-notes', {
                      ...dnSel,
                      lines: chosen.map(it => ({ itemId: it.id, quantity: parseFloat(dnQty[it.id]) })),
                    });
                    alert(he ? `נוצרה תעודה ${dnSel.noteNumber}` : `Created note ${dnSel.noteNumber}`);
                    setDnSel(null); setDnQty({}); setDnOpen({}); setSelectedItems(new Set());
                    loadDnCover();
                  } catch (err) {
                    alert(err.response?.data?.error || (he ? 'שגיאה ביצירת תעודה' : 'Failed to create note'));
                  }
                  setDnBusy(false);
                }}>
                {dnBusy ? (he ? 'שומר\u2026' : 'Saving\u2026') : (he ? '\uD83D\uDCBE צור תעודה' : 'Create note')}
              </button>
              <button className="btn btn-sm btn-outline" onClick={() => { setDnSel(null); setDnQty({}); setDnOpen({}); }}>
                {he ? 'ביטול' : 'Cancel'}
              </button>
            </div>
          </div>
        );
      })()}
      {/* Add Product Panel */}
      {showAddProduct && canEdit && (() => {
        const searchLower = addSearch.toLowerCase();
        const filtered = addStock.filter(s => (s.totalAvailable > 0 || (s.futureQty || 0) > 0) && (!searchLower ||
          (s.description || '').toLowerCase().includes(searchLower) ||
          (s.sku || '').toLowerCase().includes(searchLower)
        ));
        const selected = Object.entries(addInputs).filter(([, v]) => (v.fromST || 0) + (v.fromShager || 0) > 0);
        return (
          <div style={{ padding: 12, background: '#f5f3ff', borderRadius: 8, marginBottom: 12, border: '1px solid #c4b5fd' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input className="input input-sm" placeholder="Search product, SKU..." value={addSearch} onChange={e => setAddSearch(e.target.value)} style={{ maxWidth: 250 }} />
              <div style={{ flex: 1 }} />
              {selected.length > 0 && (
                <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff', border: 'none' }}
                  onClick={async () => {
                    const items = selected.map(([sku, v]) => {
                      const s = addStock.find(x => x.sku === sku) || {};
                      // Inputs hold combined (internal + external) per owner. Draw internal first, then external.
                      const intST = s.stAvailable || 0, intShager = s.shagerAvailable || 0;
                      const stEntered = v.fromST || 0, shEntered = v.fromShager || 0;
                      return { sku, description: s.description || '', barcode: s.barcode || '',
                        fromST: Math.min(stEntered, intST), fromExtSt: Math.max(0, stEntered - intST),
                        fromShager: Math.min(shEntered, intShager), fromExtShager: Math.max(0, shEntered - intShager) };
                    }).filter(i => i.fromST > 0 || i.fromShager > 0 || i.fromExtSt > 0 || i.fromExtShager > 0);
                    if (!items.length) return;
                    try {
                      await api.post('/warehouse/add-to-order', { orderNumber: parseInt(orderNumber), items, transportMode: orderData.transport_mode || 'SEA' });
                      setShowAddProduct(false);
                      setAddInputs({});
                      reload();
                      if (onItemsChanged) onItemsChanged();
                    } catch (err) { notifyError(err); }
                  }}>
                  ➕ Add {selected.length} to Order
                </button>
              )}
            </div>
            <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table>
                <thead><tr>
                  <th>SKU</th><th>Product</th>
                  <th className="text-end" style={{ color: '#1565c0' }}>S&T</th>
                  <th className="text-end" style={{ color: '#4caf50' }}>Shager</th>
                  <th className="text-end" style={{ color: '#e65100' }}>Future</th>
                  <th className="text-end" style={{ fontSize: 10 }}>MC</th>
                  <th className="text-end" style={{ width: 70, background: '#eff6ff' }}>From S&T</th>
                  <th className="text-end" style={{ width: 70, background: '#f0fdf4' }}>From Shager</th>
                </tr></thead>
                <tbody>
                  {filtered.map(s => (
                    <tr key={s.sku}>
                      <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{s.sku}</td>
                      <td style={{ fontSize: 12 }}>{s.description}</td>
                      <td className="text-end" style={{ color: ((s.stAvailable||0)+(s.externalSt||0)) > 0 ? '#1565c0' : '#ccc' }}>{(s.stAvailable||0)+(s.externalSt||0)}</td>
                      <td className="text-end" style={{ color: ((s.shagerAvailable||0)+(s.externalShager||0)) > 0 ? '#4caf50' : '#ccc' }}>{(s.shagerAvailable||0)+(s.externalShager||0)}</td>
                      <td className="text-end" style={{ color: s.futureQty > 0 ? '#e65100' : '#ccc', fontWeight: s.futureQty > 0 ? 600 : 400 }}>{s.futureQty || 0}</td>
                      <td className="text-end" style={{ fontSize: 10, color: '#9ca3af' }}>{s.unitsPerMasterCarton > 1 ? s.unitsPerMasterCarton : ''}</td>
                      <td style={{ background: '#f8faff' }}>
                        {((s.stAvailable||0)+(s.externalSt||0)) > 0 && <input className="inline-edit" type="number" min="0" max={(s.stAvailable||0)+(s.externalSt||0)}
                          defaultValue="" placeholder="0"
                          onBlur={async e => { let v = parseInt(e.target.value) || 0; if (v > 0) { const mc = s.unitsPerMasterCarton || 0; v = await validateMasterCartonFn(v, mc, he); } e.target.value = v || ''; setAddInputs(prev => ({ ...prev, [s.sku]: { ...(prev[s.sku] || {}), fromST: v } })); }}
                          style={{ width: 60, textAlign: 'right', fontSize: 12 }} />}
                      </td>
                      <td style={{ background: '#f8fdf8' }}>
                        {((s.shagerAvailable||0)+(s.externalShager||0)) > 0 && <input className="inline-edit" type="number" min="0" max={(s.shagerAvailable||0)+(s.externalShager||0)}
                          defaultValue="" placeholder="0"
                          onBlur={async e => { let v = parseInt(e.target.value) || 0; if (v > 0) { const mc = s.unitsPerMasterCarton || 0; v = await validateMasterCartonFn(v, mc, he); } e.target.value = v || ''; setAddInputs(prev => ({ ...prev, [s.sku]: { ...(prev[s.sku] || {}), fromShager: v } })); }}
                          style={{ width: 60, textAlign: 'right', fontSize: 12 }} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Future SI Orders — zone layout like Warehouse */}
            {addFutureSI.length > 0 && (() => {
              const now = new Date();
              const daysUntilFn = (d) => d ? Math.ceil((new Date(d) - now) / 86400000) : null;
              const zoneList = [
                { key: 'green', label: '🟢 Available Now', filter: o => o.received, bg: '#e8f5e9', border: '#4caf50', color: '#2e7d32' },
                { key: 'yellow', label: '🟡 Arriving ≤30 days', filter: o => !o.received && daysUntilFn(o.arrival_date) !== null && daysUntilFn(o.arrival_date) <= 30, bg: '#fffde7', border: '#fbc02d', color: '#f57f17' },
                { key: 'orange', label: '🟠 Arriving 30-60 days', filter: o => !o.received && daysUntilFn(o.arrival_date) !== null && daysUntilFn(o.arrival_date) > 30 && daysUntilFn(o.arrival_date) <= 60, bg: '#fff3e0', border: '#ff9800', color: '#e65100' },
                { key: 'red', label: '🔴 60+ days', filter: o => !o.received && (daysUntilFn(o.arrival_date) === null || daysUntilFn(o.arrival_date) > 60), bg: '#fbe9e7', border: '#f44336', color: '#c62828' },
              ];
              return (
                <div style={{ marginTop: 16 }}>
                  {zoneList.map(z => {
                    const orders = addFutureSI.filter(z.filter);
                    if (!orders.length) return null;
                    const totalUnits = orders.reduce((s, o) => s + (o.total_units || 0), 0);
                    return (
                      <div key={z.key} style={{ marginBottom: 16 }}>
                        <div style={{ background: z.bg, borderLeft: `4px solid ${z.border}`, padding: '10px 16px', borderRadius: 6, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, color: z.color, fontSize: 14, letterSpacing: 0.5 }}>{z.label}</span>
                          <span style={{ color: z.color, fontWeight: 600, fontSize: 13 }}>{orders.length} orders · {fmtQty(totalUnits)} units</span>
                        </div>
                        {orders.map(o => {
                          const isOpen = addExpanded.has(o.order_no);
                          const skuCount = (o.items || []).length;
                          return (
                            <div key={o.order_no} className="order-group" style={{ marginBottom: 8 }}>
                              <div className="order-group-header" onClick={() => setAddExpanded(prev => { const s = new Set(prev); s.has(o.order_no) ? s.delete(o.order_no) : s.add(o.order_no); return s; })}
                                style={{ cursor: 'pointer' }}>
                                <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                                <span className="order-no">{o.order_no}</span>
                                {o.doc_type && <span className={`badge badge-${(o.doc_type || '').toLowerCase()}`}>{o.doc_type}</span>}
                                {o.origin && <span className={`badge badge-${(o.origin || '').toLowerCase()}`}>{o.origin}</span>}
                                <div style={{ flex: 1 }} />
                                {o.arrival_date && <span className="text-muted" style={{ marginInlineEnd: 8 }}>📦 {fmtDate(o.arrival_date)}</span>}
                                <span className="text-muted">{skuCount} SKUs · {fmtQty(o.total_units)} units</span>
                              </div>
                              {isOpen && o.items && (
                                <div className="order-group-body table-wrap">
                                  <table>
                                    <thead><tr>
                                      <th>{t('common.sku')}</th>
                                      <th>{t('common.product')}</th>
                                      <th className="text-end">{he ? 'זמין' : 'Available'}</th>
                                      <th className="text-end" style={{ width: 80, background: '#f5f3ff', borderBottom: '2px solid #7c3aed' }}>{he ? 'הוסף' : 'Add'}</th>
                                    </tr></thead>
                                    <tbody>
                                      {o.items.map((item, i) => {
                                        const addKey = `${o.order_no}|${item.sku}`;
                                        return (
                                        <tr key={i}>
                                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{item.sku}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10 }}>{item.barcode}</div>}</td>
                                          <td>{item.description}</td>
                                          <td className="text-end fw-bold">{fmtQty(item.quantity)}</td>
                                          <td style={{ background: '#faf5ff' }}>
                                            <input className="inline-edit" type="number" min="0" max={item.quantity}
                                              defaultValue="" placeholder="0"
                                              onBlur={e => { const v = parseInt(e.target.value) || 0; setAddInputs(prev => ({ ...prev, [addKey]: { fromSI: v, sku: item.sku, description: item.description, barcode: item.barcode || '', sourceOrder: o.order_no } })); }}
                                              style={{ width: 70, textAlign: 'right', fontSize: 12 }} />
                                          </td>
                                        </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                  {(() => {
                                    const itemsToAdd = Object.entries(addInputs).filter(([k, v]) => k.startsWith(`${o.order_no}|`) && v.fromSI > 0);
                                    return itemsToAdd.length > 0 && (
                                      <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff', border: 'none' }}
                                          onClick={async () => {
                                            const items = itemsToAdd.map(([, v]) => ({
                                              sku: v.sku, description: v.description, barcode: v.barcode,
                                              fromST: 0, fromShager: 0, fromSI: v.fromSI, sourceOrderNo: v.sourceOrder,
                                            }));
                                            try {
                                              await api.post('/warehouse/add-to-order', { orderNumber: parseInt(orderNumber), items, transportMode: orderData.transport_mode || 'SEA' });
                                              setShowAddProduct(false);
                                              setAddInputs({});
                                              reload();
                                              if (onItemsChanged) onItemsChanged();
                                            } catch (err) { notifyError(err); }
                                          }}>
                                          ➕ {he ? `הוסף ${itemsToAdd.length} מ-${o.order_no}` : `Add ${itemsToAdd.length} from ${o.order_no}`}
                                        </button>
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        );
      })()}
      {/* Selected items summary */}
      {selectedItems.size > 0 && (() => {
        const sel = items.filter(i => selectedItems.has(i.id));
        const selQty = sel.reduce((s, i) => s + parseFloat(i.quantity), 0);
        const selCBM = sel.reduce((s, i) => s + parseFloat(i.quantity) * parseFloat(i.cbm_per_unit || 0), 0);
        const selWkg = sel.reduce((s, i) => s + parseFloat(i.quantity) * parseFloat(i.weight_kg || 0), 0);
        const selValue = sel.reduce((s, i) => s + parseFloat(i.quantity) * (parseFloat(i.selling_price) || 0), 0);
        const selPallets = Math.ceil(selCBM / PALLET_CBM);
        const CONTAINER_CBM = 67.25;
        const selContainers = selCBM > 0 ? Math.ceil(selCBM / CONTAINER_CBM) : 0;
        return (
          <div style={{ display: 'flex', gap: 16, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
            <span style={{ fontWeight: 700, color: '#1565c0' }}>{selectedItems.size} {he ? 'נבחרו' : 'selected'}</span>
            <span>{fmtQty(selQty)} {he ? 'יחידות' : 'units'}</span>
            <span>{selCBM.toFixed(2)} CBM</span>
            <span>{selPallets} {he ? 'משטחים' : 'pallets'}</span>
            <span>{selContainers}×40HC</span>
            <span>{(selWkg / 1000).toFixed(2)}t</span>
            <span style={{ fontWeight: 700 }}>${selValue.toLocaleString()}</span>
          </div>
        );
      })()}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {canEdit && <th style={{ width: 30, textAlign: 'center' }}><input type="checkbox" checked={selectedItems.size === items.length} onChange={toggleAll} /></th>}
              <th style={{ width: 70, textAlign: 'center' }}>Pick From</th>
              <th style={{ width: 90, cursor: 'pointer' }} onClick={() => setItemSort(p => ({ field: 'sku', dir: p.field === 'sku' && p.dir === 'asc' ? 'desc' : 'asc' }))}>SKU{itemSort.field === 'sku' ? (itemSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</th>
              <th style={{ minWidth: 200, cursor: 'pointer' }} onClick={() => setItemSort(p => ({ field: 'description', dir: p.field === 'description' && p.dir === 'asc' ? 'desc' : 'asc' }))}>Product{itemSort.field === 'description' ? (itemSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</th>
              <th style={{ width: 70, textAlign: 'center', cursor: 'pointer' }} onClick={() => setItemSort(p => ({ field: 'quantity', dir: p.field === 'quantity' && p.dir === 'desc' ? 'asc' : 'desc' }))}>Qty{itemSort.field === 'quantity' ? (itemSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</th>
              <th style={{ width: 92, textAlign: 'center', fontSize: 11, color: '#0f766e' }}>{he ? 'בתעודות' : 'On notes'}</th>
              <th style={{ width: 60, textAlign: 'center', fontSize: 11 }}>CBM</th>
              <th style={{ width: 100, textAlign: 'center' }}>Price</th>
              <th style={{ width: 120, textAlign: 'center' }}>Total</th>
              {showPricing && <>
                <th style={{ width: 80, textAlign: 'center', color: '#7b1fa2', fontSize: 11 }}>M Price</th>
                <th style={{ width: 80, textAlign: 'center', color: '#1565c0', fontSize: 11 }}>C Price</th>
                <th style={{ width: 80, textAlign: 'center', color: '#2e7d32', fontSize: 11 }}>Shager</th>
                <th style={{ width: 80, textAlign: 'center', color: '#e65100', fontSize: 11 }}>Profit/u</th>
              </>}
              <th style={{ width: 40, textAlign: 'center' }}>Zone</th>
              <th style={{ width: 60, textAlign: 'center' }}>Transport</th>
              <th style={{ width: 110, fontSize: 11 }}>Barcode</th>
            </tr>
          </thead>
          <tbody>
            {[...items].sort((a, b) => {
              if (!itemSort.field) return 0;
              let va, vb;
              if (itemSort.field === 'quantity') { va = parseFloat(a.quantity) || 0; vb = parseFloat(b.quantity) || 0; }
              else { va = (a[itemSort.field] || '').toLowerCase(); vb = (b[itemSort.field] || '').toLowerCase(); }
              if (va < vb) return itemSort.dir === 'asc' ? -1 : 1;
              if (va > vb) return itemSort.dir === 'asc' ? 1 : -1;
              return 0;
            }).map((item, i) => {
              const price = priceEdits[item.id] !== undefined ? priceEdits[item.id] : (item.selling_price || '');
              const numPrice = parseFloat(price) || 0;
              return (
                <tr key={item.id} style={selectedItems.has(item.id) ? { background: 'rgba(198,40,40,0.05)' } : {}}>
                  {canEdit && <td style={{ textAlign: 'center' }}><input type="checkbox" checked={selectedItems.has(item.id)} onChange={() => toggleItem(item.id)} /></td>}
                  <td style={{ textAlign: 'center' }}>{(() => {
                    const s = item.source_order_no;
                    if (s === 'EXISTING-STOCK' || s === 'S&T') return <span className="badge" style={{ background: '#e3f2fd', color: '#1565c0', fontSize: 11, fontWeight: 700 }}>S&T</span>;
                    if (s === 'SHAGER-STOCK') return <span className="badge" style={{ background: '#fff3e0', color: '#e65100', fontSize: 11, fontWeight: 700 }}>Shager</span>;
                    return <span className="text-muted" style={{ fontSize: 11 }}>{s}</span>;
                  })()}</td>
                  <td>{item.sku}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10 }}>{item.barcode}</div>}</td>
                  <td>{item.description}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>
                    {canEdit ? (
                      <input className="inline-edit" type="number" step="1" style={{ width: 60, textAlign: 'center', fontWeight: 700 }}
                        defaultValue={parseFloat(item.quantity)}
                        onBlur={async (e) => {
                          const newQty = parseFloat(e.target.value);
                          const oldQty = parseFloat(item.quantity);
                          if (!newQty || newQty <= 0 || newQty === oldQty) { e.target.value = oldQty; return; }
                          const msg = newQty < oldQty
                            ? `${he ? 'הקטן' : 'Reduce'} ${item.sku} ${he ? 'מ' : 'from'}-${oldQty} ${he ? 'ל' : 'to'}-${newQty}? (${oldQty - newQty} ${he ? 'יחזרו למלאי' : 'units return to stock'})`
                            : `${he ? 'הגדל' : 'Increase'} ${item.sku} ${he ? 'מ' : 'from'}-${oldQty} ${he ? 'ל' : 'to'}-${newQty}? (${newQty - oldQty} ${he ? 'יילקחו מהמלאי' : 'units taken from stock'})`;
                          if (!(await confirm({ body: msg }))) { e.target.value = oldQty; return; }
                          try {
                            await api.put(`/warehouse/cellcom-order-item/${item.id}/qty`, { quantity: newQty });
                            reload();
                            if (onItemsChanged) onItemsChanged();
                          } catch (err) { notifyError(err); e.target.value = oldQty; }
                        }}
                        onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                      />
                    ) : fmtQty(parseFloat(item.quantity))}
                    {(() => {
                      const qty = parseFloat(item.quantity);
                      const mc = parseInt(item.units_per_master_carton) || 0;
                      if (mc <= 1 || qty % mc === 0) return null;
                      const lower = Math.floor(qty / mc) * mc;
                      const upper = Math.ceil(qty / mc) * mc;
                      return (
                        <div style={{ marginTop: 2 }}>
                          <select className="inline-edit" style={{ width: 70, fontSize: 10, color: '#dc2626', fontWeight: 600, background: '#fef2f2' }}
                            defaultValue=""
                            onChange={async (e) => {
                              const newQty = parseInt(e.target.value);
                              if (!newQty) return;
                              try {
                                await api.put(`/warehouse/cellcom-order-item/${item.id}/qty`, { quantity: newQty });
                                reload();
                                if (onItemsChanged) onItemsChanged();
                              } catch (err) { notifyError(new Error('Unknown')); }
                            }}>
                            <option value="">⚠ MC:{mc}</option>
                            {lower > 0 && <option value={lower}>↓ {lower}</option>}
                            <option value={upper}>↑ {upper}</option>
                          </select>
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 11 }}>{(() => {
                    const c = dnCover[item.id];
                    if (!c || c.assigned <= 0) return <span className="text-muted">—</span>;
                    const full = c.remaining <= 0.001;
                    return (
                      <span style={{ fontWeight: 700, color: full ? '#15803d' : '#b45309' }}
                        title={he ? `נותרו ${fmtQty(c.remaining)} לשיבוץ` : `${fmtQty(c.remaining)} still unassigned`}>
                        {fmtQty(c.assigned)}{full ? ' ✓' : ` / ${fmtQty(item.quantity)}`}
                      </span>
                    );
                  })()}</td>
                  <td style={{ textAlign: 'center', fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#94a3b8' }}>
                    {(parseFloat(item.quantity) * parseFloat(item.cbm_per_unit || 0)).toFixed(3)}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {canEdit ? (
                      <input className="inline-edit" type="number" step="0.01" style={{ width: 90, textAlign: 'center' }}
                        value={price}
                        onChange={e => setPriceEdits(prev => ({ ...prev, [item.id]: e.target.value }))}
                        onBlur={e => savePrice(item.id, e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                      />
                    ) : (numPrice ? `$${numPrice.toFixed(2)}` : '—')}
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{numPrice ? `$${(parseFloat(item.quantity) * numPrice).toFixed(2)}` : '—'}</td>
                  {showPricing && (() => {
                    const pi = pricingData?.items?.find(p => p.sku === item.sku);
                    if (!pi) return <><td style={{ textAlign: 'center' }} className="text-muted">—</td><td style={{ textAlign: 'center' }} className="text-muted">—</td><td style={{ textAlign: 'center' }} className="text-muted">—</td><td style={{ textAlign: 'center' }} className="text-muted">—</td></>;
                    const profitPerUnit = pi.cPrice - pi.purchaseLoaded;
                    return (<>
                      <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: '#7b1fa2', fontSize: 12 }}>${pi.mPrice.toFixed(2)}</td>
                      <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: '#1565c0', fontSize: 12 }}>${pi.cPrice.toFixed(2)}</td>
                      <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: '#2e7d32', fontSize: 12 }}>${pi.cPrice.toFixed(2)}</td>
                      <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: profitPerUnit >= 0 ? '#2e7d32' : '#c62828', fontSize: 12, fontWeight: 700 }}>${profitPerUnit.toFixed(2)}</td>
                    </>);
                  })()}
                  <td style={{ textAlign: 'center' }}><span style={{ fontSize: 11 }}>{item.zone === 'green' ? '🟢' : item.zone === 'yellow' ? '🟡' : item.zone === 'orange' ? '🟠' : '🔴'}</span></td>
                  <td style={{ textAlign: 'center', fontSize: 12 }}>{item.transport_mode || '—'}</td>
                  <td className="text-muted" style={{ fontSize: 11 }}>{item.barcode}</td>
                </tr>
              );
            })}
            {(() => {
              const totalQty = items.reduce((s, i) => s + parseFloat(i.quantity), 0);
              const totalValue = items.reduce((s, i) => s + parseFloat(i.quantity) * (parseFloat(priceEdits[i.id] !== undefined ? priceEdits[i.id] : i.selling_price) || 0), 0);
              const totalCBM = items.reduce((s, i) => s + parseFloat(i.quantity) * parseFloat(i.cbm_per_unit || 0), 0);
              const totalWkg = items.reduce((s, i) => s + parseFloat(i.quantity) * parseFloat(i.weight_kg || 0), 0);
              const totalPallets = Math.ceil(totalCBM / PALLET_CBM);
              const totalContainers = totalPallets > 0 ? Math.ceil(totalPallets / PALLETS_PER_40HC) : 0;
              // 11 body columns now: Pick From, SKU, Product, Qty, On notes, CBM, Price,
              // Total, Zone, Transport, Barcode (+4 when pricing is shown). The checkbox
              // is emitted separately below and is not counted here.
              const totalCols = 11 + (showPricing ? 4 : 0);
              return (
                <>
                  <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                    {canEdit && <td></td>}
                    <td colSpan={3}>{t('common.total')}</td>
                    <td style={{ textAlign: 'center' }}>{fmtQty(totalQty)}</td>
                    <td></td>{/* On notes */}
                    <td></td>{/* CBM */}
                    <td></td>{/* Price */}
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>${totalValue.toFixed(2)}</td>
                    <td colSpan={totalCols - 8}></td>
                  </tr>
                  {showPricing && pricingData?.items?.length > 0 && (() => {
                    const mPct = (Number.isFinite(pricingData.mPercentage) ? pricingData.mPercentage : 1.75) / 100; // M%=0 fix: honor 0
                    let mP = 0, cP = 0, sP = 0;
                    for (const pi of pricingData.items) {
                      const q = parseFloat(pi.quantity) || 0;
                      mP += (pi.customerPrice * mPct) * q;            // M margin = price × M%
                      cP += (pi.mPrice - pi.cPrice) * q;              // C layer = M price − C price
                      sP += (pi.cPrice - pi.purchaseLoaded) * q;      // Shager = C price − loaded cost
                    }
                    const chain = mP + cP + sP;
                    const fmt = n => `$${(Math.round(n * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    const cell = (label, val, color) => (
                      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', padding: '2px 16px' }}>
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{label}</span>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, color: val < 0 ? '#c62828' : color, fontSize: 14 }}>{fmt(val)}</span>
                      </span>
                    );
                    return (
                      <tr style={{ background: '#f8fafc', borderTop: '1px solid var(--border)' }}>
                        {canEdit && <td></td>}
                        <td colSpan={totalCols} style={{ padding: '8px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, color: '#0f172a', marginInlineEnd: 8 }}>💰 {he ? 'רווח בהזמנה' : 'Order Profit'}</span>
                            {cell(he ? 'רווח M' : 'M Profit', mP, '#7b1fa2')}
                            <span style={{ color: '#cbd5e1' }}>·</span>
                            {cell(he ? 'רווח C' : 'C Profit', cP, '#1565c0')}
                            <span style={{ color: '#cbd5e1' }}>·</span>
                            {cell(he ? 'רווח שגר' : 'Shager Profit', sP, '#2e7d32')}
                            <span style={{ color: '#cbd5e1' }}>·</span>
                            {cell(he ? 'רווח שרשרת' : 'Chain Total', chain, '#0f172a')}
                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                  {(() => {
                    const isAir = (orderData.transport_mode || '').toUpperCase() === 'AIR';
                    if (isAir) {
                      const totalMasterCartons = items.reduce((s, i) => {
                        const qty = parseFloat(i.quantity);
                        const perMaster = parseInt(i.units_per_master_carton) || 1;
                        return s + Math.ceil(qty / perMaster);
                      }, 0);
                      return (<>
                        <tr style={{ background: '#fff3e0' }}>
                          {canEdit && <td></td>}
                          <td colSpan={totalCols} style={{ fontWeight: 600, color: '#e65100', padding: '10px 16px' }}>
                            ✈️ Air Shipping — {totalCBM.toFixed(3)} CBM · {totalMasterCartons} master cartons · {totalWkg.toFixed(2)} kg
                          </td>
                        </tr>
                        <tr style={{ background: '#fff8e1', fontSize: 12 }}>
                          {canEdit && <td></td>}
                          <td colSpan={totalCols} style={{ padding: '4px 16px' }}>
                            {items.map(i => {
                              const qty = parseFloat(i.quantity);
                              const perMaster = parseInt(i.units_per_master_carton) || 1;
                              const cartons = Math.ceil(qty / perMaster);
                              return (
                                <span key={i.sku} style={{ display: 'inline-block', marginRight: 12, padding: '2px 8px', background: '#ffe0b2', borderRadius: 4, marginBottom: 2 }}>
                                  <strong>{i.sku}</strong>: {cartons} ctn ({perMaster}/ctn × {fmtQty(qty)})
                                </span>
                              );
                            })}
                          </td>
                        </tr>
                      </>);
                    }
                    return (<>
                      <tr style={{ background: '#f0f7ff' }}>
                        {canEdit && <td></td>}
                        <td colSpan={totalCols} style={{ fontWeight: 600, color: '#1565c0', padding: '10px 16px' }}>
                          🚢 Ocean Shipping — {totalCBM.toFixed(2)} CBM · {totalPallets} pallets · {totalWkg.toFixed(2)} kg
                        </td>
                      </tr>
                      {totalPallets > 0 && (() => {
                        const exactContainers = Math.ceil(totalPallets / 20);
                        const bufferContainers = Math.ceil(totalPallets / PALLETS_PER_40HC);
                        return (<>
                          <tr style={{ background: '#e8f5e9' }}>
                            {canEdit && <td></td>}
                            <td colSpan={totalCols} style={{ padding: '8px 16px' }}>
                              <span style={{ fontWeight: 600, color: '#2e7d32', marginRight: 16 }}>📦 With Pallets (20p/container) — {exactContainers} × 40HC</span>
                              <span style={{ fontSize: 12, color: '#2e7d32' }}>
                              {Array.from({ length: exactContainers }, (_, c) => {
                                const p = c < exactContainers - 1 ? 20 : totalPallets - (exactContainers - 1) * 20;
                                const cbm = p * PALLET_CBM;
                                const wkg = totalWkg > 0 ? (totalWkg * p / totalPallets) : 0;
                                return (
                                  <div key={c} style={{ display: 'inline-block', marginRight: 12, padding: '2px 8px', background: '#c8e6c9', borderRadius: 4, marginBottom: 2 }}>
                                    <strong>#{c + 1}</strong>: {p}p · {cbm.toFixed(1)} CBM{wkg > 0 ? ` · ${wkg.toFixed(2)} kg` : ''} <span style={{ fontSize: 10, color: '#546e7a' }}>({(p / 20 * 100).toFixed(0)}%)</span>
                                  </div>
                                );
                              })}
                              </span>
                            </td>
                          </tr>
                          <tr style={{ background: '#e3f2fd' }}>
                            {canEdit && <td></td>}
                            <td colSpan={totalCols} style={{ padding: '8px 16px' }}>
                              {(() => {
                                const CONTAINER_CBM = 67.25;
                                const looseContainers = Math.ceil(totalCBM / CONTAINER_CBM);
                                const fillPct = Math.round((totalCBM / (looseContainers * CONTAINER_CBM)) * 100);
                                return (
                                  <span style={{ fontWeight: 600, color: '#0d47a1' }}>
                                    🚛 Without Pallets — {looseContainers} × 40HC · {totalCBM.toFixed(1)} / {(looseContainers * CONTAINER_CBM).toFixed(1)} CBM · {fillPct}% full
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                        </>);
                      })()}
                    </>);
                  })()}
                </>
              );
            })()}
        </tbody>
      </table>
    </div>
    </div>
  );
}

export function CellcomOrdersPage() {
  return <WarehousePage mode="cellcom" />;
}

export default function WarehousePage({ mode = 'warehouse' }) {
  const TABS = mode === 'cellcom' ? CELLCOM_TABS : WAREHOUSE_TABS;
  const { t, i18n } = useTranslation();
  const he = i18n.language === 'he';
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'warehouse');
  const [tab, setTab] = useState(mode === 'cellcom' ? 'cellcomOrders' : 'allocations');
  const [chainTotals, setChainTotals] = useState(null);   // {mP, cP, sP, chain, revenue, units, orders}

  // Chain margin summed across every non-cancelled Cellcom order. Uses the SAME per-item
  // formulas as the single-order profit band below, so the summary can never drift from
  // what an individual order reports.
  const loadChainTotals = useCallback(async () => {
    if (mode !== 'cellcom') return;
    try {
      const res = await api.get('/profitability/cellcom-orders');
      const all = Array.isArray(res.data) ? res.data : (res.data.orders || []);
      const list = all.filter(o => o.status !== 'cancelled');
      let mP = 0, cP = 0, sP = 0, revenue = 0, units = 0, mPriceTot = 0, cPriceTot = 0;
      for (const o of list) {
        const mPct = (Number.isFinite(o.mPercentage) ? o.mPercentage : 1.75) / 100;
        for (const pi of (o.items || [])) {
          const q = parseFloat(pi.quantity) || 0;
          mP += (pi.customerPrice * mPct) * q;
          cP += (pi.mPrice - pi.cPrice) * q;
          sP += (pi.cPrice - pi.purchaseLoaded) * q;
          revenue += (parseFloat(pi.customerPrice) || 0) * q;
          mPriceTot += (parseFloat(pi.mPrice) || 0) * q;
          cPriceTot += (parseFloat(pi.cPrice) || 0) * q;
          units += q;
        }
      }
      setChainTotals({ mP, cP, sP, chain: mP + cP + sP, revenue, mPriceTot, cPriceTot, units, orders: list.length });
    } catch { setChainTotals(null); }
  }, [mode]);
  useEffect(() => { loadChainTotals(); }, [loadChainTotals]);
  // One definition, rendered by both the Cellcom-orders and All-orders tabs, so the two
  // summaries can never drift apart.
  const renderChainBand = () => (chainTotals ? (() => {
            const money = n => `$${(Math.round(n * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
            const box = (label, val, color, pctOf, baseLabel) => (
              <div style={{ flex: '1 1 160px', background: '#fff', border: `1px solid ${color}33`,
                            borderTop: `3px solid ${color}`, borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: "'DM Mono', monospace" }}>{money(val)}</div>
                {pctOf > 0 && (
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {((val / pctOf) * 100).toFixed(2)}% <span style={{ fontSize: 10 }}>{baseLabel}</span>
                  </div>
                )}
              </div>
            );
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {box(he ? 'רווח M' : 'M Profit', chainTotals.mP, '#7b1fa2', chainTotals.revenue,
                       he ? 'ממכירות סלקום' : 'of Cellcom price')}
                  {box(he ? 'רווח C' : 'C Profit', chainTotals.cP, '#1565c0', chainTotals.cPriceTot,
                       he ? 'ממחיר C' : 'of C price')}
                  {box(he ? 'רווח שגר' : 'Shager Profit', chainTotals.sP, '#2e7d32', chainTotals.cPriceTot,
                       he ? 'ממחיר C' : 'of C price')}
                  {box(he ? 'סה"כ שרשרת' : 'Chain Total', chainTotals.chain, '#0f172a', chainTotals.revenue,
                       he ? 'ממכירות סלקום' : 'of Cellcom price')}
                  {box(he ? 'מכירות לסלקום' : 'Cellcom Revenue', chainTotals.revenue, '#64748b', 0, '')}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  {he ? `${chainTotals.orders} הזמנות · ${fmtQty(chainTotals.units)} יח' · לא כולל מבוטלות`
                      : `${chainTotals.orders} orders · ${fmtQty(chainTotals.units)} units · excludes cancelled`}
                </div>
              </div>
            );
          })() : null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [stockForm, setStockForm] = useState({ sku: '', description: '', quantity: '', unitCost: '', origin: 'EU', notes: '' });
  const [cellcomOrders, setCellcomOrders] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);
  const [createTransport, setCreateTransport] = useState('SEA');
  const [addToOrderNum, setAddToOrderNum] = useState('');
  const [orderInputs, setOrderInputs] = useState({}); // {sku: {fromST: 0, fromShager: 0}}
  const [orderSearch, setOrderSearch] = useState('');
  const [futureInputs, setFutureInputs] = useState({}); // {orderNo|sku: qty}
  const [futureStockInputs, setFutureStockInputs] = useState({}); // {sku: {fromST: 0, fromShager: 0}}
  const [futureSearch, setFutureSearch] = useState('');
  const [incomingOrders, setIncomingOrders] = useState([]);
  const [editingShipping, setEditingShipping] = useState(null);
  const [stockSort, setStockSort] = useState({ field: 'sku', dir: 'asc' });
  const [stockFilter, setStockFilter] = useState('all');
  const [stockAddMode, setStockAddMode] = useState(false);
  const [stockAddInputs, setStockAddInputs] = useState({}); // { sku: { fromST, fromShager } }
  const [stockAddTarget, setStockAddTarget] = useState('');
  const [selectedOrderNos, setSelectedOrderNos] = useState(new Set());
  const [cellcomItemsCache, setCellcomItemsCache] = useState({}); // { orderNumber: items[] }

  // Master carton validation: returns adjusted qty (async — opens confirm modal if MC mismatch).
  const validateMasterCarton = async (qty, sku, stockData) => {
    if (!qty || qty <= 0) return qty;
    const item = (stockData || stockSummary).find(s => s.sku === sku);
    const mc = item?.unitsPerMasterCarton || parseInt(item?.units_per_master_carton) || 0;
    return await validateMasterCartonFn(qty, mc, he);
  };
  const fetchTab = useCallback(async (silent) => {
    // Tabs with self-managed data fetching (new shared views handle their own loading)
    if (tab === 'extWarehouse' || tab === 'pullOrders' || tab === 'qrSerials') {
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      if (tab === 'allocations') {
        const [res, stockRes, coRes] = await Promise.all([api.get('/warehouse'), api.get('/warehouse/stock-summary'), api.get('/warehouse/cellcom-orders')]);
        setData(res.data || []);
        setStockSummary(stockRes.data || []);
        setCellcomOrders(coRes.data || []);
      } else if (tab === 'newOrder') {
        const res = await api.get('/warehouse/stock-summary');
        setStockSummary(res.data || []);
      } else if (tab === 'futureOrder') {
        const [stockRes, whRes] = await Promise.all([api.get('/warehouse/stock-summary'), api.get('/warehouse')]);
        setStockSummary(stockRes.data || []);
        // Filter to only incoming orders (not green/stock)
        setIncomingOrders((whRes.data || []).filter(o => o.zone !== 'green' && o.docType === 'SI'));
      } else if (tab === 'cellcomOrders' || tab === 'completed' || tab === 'allOrders') {
        const res = await api.get('/warehouse/cellcom-orders');
        setCellcomOrders(res.data || []);
        if (tab === 'completed') {
          const handledRes = await api.get('/warehouse/handled', { params: { archived: false } });
          setData(handledRes.data.items || handledRes.data || []);
        }
      } else {
        const res = await api.get('/warehouse/handled', { params: { archived: tab === 'archived' } });
        setData(res.data.items || res.data || []);
      }
    } catch {} finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchTab(); setExpanded(new Set()); setSelected(new Set()); }, [fetchTab]);

  const toggle = (no) => setExpanded(prev => {
    const n = new Set(prev); n.has(no) ? n.delete(no) : n.add(no); return n;
  });

  const updateEdit = (orderNo, sku, field, value) => {
    const key = `${orderNo}|${sku}`;
    setEdits(prev => ({ ...prev, [key]: { ...prev[key], orderNo, sku, [field]: value } }));
  };

  const saveAllocation = async (orderNo, sku, item) => {
    const key = `${orderNo}|${sku}`;
    const edit = edits[key] || {};
    try {
      await api.put('/warehouse/allocate', {
        orderNo, sku,
        cellcomQty: edit.cellcomQty ?? item.cellcomQty ?? 0,
        transportMode: edit.transportMode ?? item.transportMode ?? '',
        sticker: edit.sticker ?? item.sticker ?? false,
        notes: edit.notes ?? item.notes ?? '',
      });
      fetchTab(true);
      setEdits(prev => { const n = { ...prev }; delete n[key]; return n; });
    } catch {}
  };

  const handleMark = async () => {
    const items = Array.from(selected).map(k => {
      const [orderNo, sku] = k.split('|');
      return { orderNo, sku };
    });
    if (!items.length) return;
    try {
      await api.post('/warehouse/handle', { items });
      setSelected(new Set());
      fetchTab(true);
    } catch {}
  };

  const handleArchive = async (orderNo, sku, archive) => {
    try {
      await api.put(`/warehouse/archive/${orderNo}/${encodeURIComponent(sku)}`, { archive });
      fetchTab(true);
    } catch {}
  };

  const pickAllLines = () => {
    const allKeys = new Set();
    for (const order of data) {
      for (const item of (order.items || [])) {
        const cellcom = item.cellcomQty || 0;
        if (cellcom > 0) {
          allKeys.add(`${order.orderNo}|${item.sku}`);
        }
      }
    }
    if (allKeys.size === 0) {
      notifyMessage('No items with Cellcom allocation to pick', 'אין פריטים עם הקצאה לסלקום ללקיטה');
      return;
    }
    setSelected(allKeys);
  };

  const exportAllAllocations = () => {
    const rows = stockSummary.map(item => ({
      'SKU': item.sku,
      'Product': item.description,
      'S&T Stock': (item.stAvailable || 0) + (item.externalSt || 0),
      'Shager Stock': (item.shagerAvailable || 0) + (item.externalShager || 0),
      'Total Available': item.totalAvailable || 0,
      'Min S&T': item.minStockST || 0,
      'Min Shager': item.minStockShager || 0,
      'Min Keep': (item.minStockST || 0) + (item.minStockShager || 0),
      'Barcode': item.barcode || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{wch:12},{wch:35},{wch:12},{wch:14},{wch:16},{wch:10},{wch:12},{wch:10},{wch:16}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Summary');
    XLSX.writeFile(wb, 'stock_summary.xlsx');
  };

  const exportSelected = () => {
    if (selected.size === 0) return;
    const rows = [];
    for (const order of data) {
      for (const item of (order.items || [])) {
        const key = `${order.orderNo}|${item.sku}`;
        if (!selected.has(key)) continue;
        rows.push({
          'Order': order.orderNo,
          'SKU': item.displaySku || item.sku,
          'Product': item.description,
          'Total': item.totalQty,
          'Cellcom Qty': item.cellcomQty || 0,
          'Transport': item.transportMode || '',
          'Left Stock': (item.totalQty || 0) - (item.cellcomQty || 0),
        });
      }
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Selected');
    XLSX.writeFile(wb, 'warehouse_selected.xlsx');
  };

  const exportExcel = () => {
    const rows = data.map(item => ({
      'Order': item.order_no,
      'SKU': item.sku,
      'Description': item.description,
      'Quantity': item.quantity,
      'Barcode': item.barcode,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'PickList');
    XLSX.writeFile(wb, 'picklist.xlsx');
  };

  const toggleSelect = (key) => setSelected(prev => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  // Existing stock handlers
  const addExistingStock = async (e) => {
    e.preventDefault();
    if (!stockForm.sku || !stockForm.quantity) return;
    try {
      await api.post('/warehouse/existing-stock', stockForm);
      setStockForm({ sku: '', description: '', quantity: '', unitCost: '', origin: 'EU', notes: '' });
      fetchTab(true);
    } catch {}
  };

  const uploadStockExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const wb = XLSX.read(ev.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      const items = rows.filter(r => r.SKU && r.Quantity).map(r => ({
        sku: String(r.SKU),
        description: r.Product || r.Description || '',
        quantity: parseFloat(r.Quantity) || 0,
        unitCost: r['Unit Cost'] ? parseFloat(r['Unit Cost']) : null,
        origin: r.Origin || 'EU',
        notes: r.Notes || '',
      }));
      if (!items.length) return notifyMessage('No valid rows found. Need columns: SKU, Quantity', 'לא נמצאו שורות תקינות. נדרשות עמודות: SKU, Quantity');
      try {
        const res = await api.post('/warehouse/existing-stock/bulk', { items });
        notifyMessage(`Added ${res.data.added} items`, `נוספו ${res.data.added} פריטים`);
        fetchTab(true);
      } catch { notifyMessage('Upload failed', 'העלאה נכשלה'); }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  // Cellcom order creation from pick list
  const createCellcomOrder = async () => {
    if (selected.size === 0) return;
    const items = data.filter(item => selected.has(`${item.order_no}|${item.sku}`)).map(item => ({
      sourceOrderNo: item.order_no,
      sku: item.sku,
      description: item.description,
      quantity: parseFloat(item.quantity),
      zone: item.zone || (item.arrival_date && new Date(item.arrival_date) <= new Date() ? 'green' : 'yellow'),
      barcode: item.barcode || '',
      transportMode: item.transport_mode || createTransport,
    }));
    if (!items.length) return;
    try {
      const res = await api.post('/warehouse/cellcom-order', { items, transportMode: createTransport });
      notifyMessage(`Order #${res.data.orderNumber} created (${res.data.status}) — ${res.data.itemCount} items`, `הזמנה #${res.data.orderNumber} נוצרה (${res.data.status}) — ${res.data.itemCount} פריטים`);
      setSelected(new Set());
      setTab('cellcomOrders');
    } catch (err) { notifyError(err); }
  };

  const sendToCustomer = async (orderNumber) => {
    if (!(await confirm({ body: he ? `לשלוח הזמנה #${orderNumber} לפורטל הלקוח?` : `Send order #${orderNumber} to customer portal?` }))) return;
    try {
      await api.put(`/warehouse/cellcom-orders/${orderNumber}/send`);
      fetchTab(true);
      const res2 = await api.get('/warehouse/cellcom-orders');
      setCellcomOrders(res2.data);
    } catch (err) { notifyError(err); }
  };

  const handleUnship = async (orderNumber) => {
    if (!(await confirm({
      body: he ? `לבטל סטטוס "נשלח" עבור הזמנה #${orderNumber}? יחזור ל-"נלקט".` : `Cancel shipped status for order #${orderNumber}? It will revert to "picked".`,
      danger: true,
    }))) return;
    try {
      await api.put(`/warehouse/cellcom-orders/${orderNumber}/unship`);
      fetchTab(true);
      const res2 = await api.get('/warehouse/cellcom-orders');
      setCellcomOrders(res2.data);
    } catch (err) { notifyError(err); }
  };

  const handleUpdateShipping = async (orderNumber) => {
    if (!editingShipping) return;
    try {
      await api.put(`/warehouse/cellcom-orders/${orderNumber}/shipping`, {
        estimatedArrival: editingShipping.estimatedArrival || null,
        shippingMethod: editingShipping.shippingMethod || null,
        shippingDate: editingShipping.shippingDate || null,
        arrivalDate: editingShipping.arrivalDate || null,
      });
      setEditingShipping(null);
      const res2 = await api.get('/warehouse/cellcom-orders');
      setCellcomOrders(res2.data);
    } catch (err) { notifyError(err); }
  };

  const deleteStock = async (id) => {
    if (!(await confirm({ body: he ? 'למחוק פריט מלאי זה?' : 'Delete this stock item?', danger: true }))) return;
    try {
      await api.delete(`/warehouse/existing-stock/${id}`);
      fetchTab(true);
    } catch {}
  };

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;

  // Group orders by zone for allocations tab
  const zones = ['green', 'yellow', 'orange', 'red'];
  const ordersByZone = {};
  if (tab === 'allocations') {
    for (const z of zones) ordersByZone[z] = [];
    for (const order of data) {
      const z = order.zone || 'red';
      if (ordersByZone[z]) ordersByZone[z].push(order);
    }
  }

  const renderOrderTable = (order) => {
    const totalUnits = (order.items || []).reduce((s, i) => s + (i.totalQty || 0), 0);
    return (
      <div key={order.orderNo} className="order-group" style={{ marginBottom: 8 }}>
        <div className="order-group-header" onClick={() => toggle(order.orderNo)}>
          <span className={`arrow${expanded.has(order.orderNo) ? ' open' : ''}`}>&#9654;</span>
          <span className="order-no">{order.orderNo}</span>
          {order.docType && order.docType !== 'STOCK' && <span className={`badge badge-${(order.docType || '').toLowerCase()}`}>{order.docType}</span>}
          {order.docType === 'STOCK' && <span className="badge" style={{ background: '#4caf50', color: '#fff' }}>EXISTING</span>}
          {order.origin && <span className={`badge badge-${(order.origin || '').toLowerCase()}`}>{order.origin}</span>}
          {order.orderMonth && <span className="text-muted">{order.orderMonth}</span>}
          <div style={{ flex: 1 }} />
          {order.arrivalDate && <span className="text-muted" style={{ marginInlineEnd: 8 }}>📦 {fmtDate(order.arrivalDate)}</span>}
          <span className="text-muted">{(order.items || []).length} SKUs · {fmtQty(totalUnits)} units</span>
          {canEdit && order.docType !== 'STOCK' && !order.received && (
            <button className="btn btn-sm" style={{ marginInlineStart: 8, background: '#4caf50', color: '#fff', border: 'none' }}
              onClick={async (e) => { e.stopPropagation(); if (!(await confirm({ body: he ? `לקבל הזמנה ${order.orderNo} למלאי שאגר?` : `Receive order ${order.orderNo} into Shager Stock?` }))) return; try { await api.post('/warehouse/receive', { orderNo: order.orderNo }); fetchTab(true); } catch (err) { notifyError(err); } }}>
              {t('warehouse.receiveToStock')}
            </button>
          )}
          {order.received && (
            <>
              <span className="badge" style={{ background: '#4caf50', color: '#fff', marginInlineStart: 8 }}>✓ In Stock</span>
              {canEdit && (
                <button className="btn btn-sm" style={{ marginInlineStart: 4, background: '#c62828', color: '#fff', border: 'none', fontSize: 10 }}
                  onClick={async (e) => { e.stopPropagation(); if (!(await confirm({ body: he ? `להסיר הזמנה ${order.orderNo} ממלאי שאגר?` : `Remove order ${order.orderNo} from Shager Stock?`, danger: true }))) return; try { await api.delete(`/warehouse/receive/${order.orderNo}`); fetchTab(true); } catch (err) { notifyError(err); } }}>
                  ✕
                </button>
              )}
            </>
          )}
        </div>
        {expanded.has(order.orderNo) && (
          <div className="order-group-body table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 30 }}></th>
                  <th>{t('common.sku')}</th>
                  <th>{t('common.product')}</th>
                  <th className="text-end">{t('common.total')}</th>
                  <th colSpan="3" className="text-center" style={{ borderBottom: '2px solid var(--gold, #C8A22C)', background: 'rgba(200,162,44,0.06)' }}>
                    Cellcom
                  </th>
                  <th className="text-end">Left Stock</th>
                  <th>Notes</th>
                  {canEdit && <th></th>}
                </tr>
                <tr>
                  <th></th><th></th><th></th><th></th>
                  <th className="text-end" style={{ background: 'rgba(200,162,44,0.06)', fontSize: 11 }}>Qty</th>
                  <th style={{ background: 'rgba(200,162,44,0.06)', fontSize: 11 }}>{t('warehouse.transport')}</th>
                  <th className="text-center" style={{ background: 'rgba(200,162,44,0.06)', fontSize: 11 }}>Sticker</th>
                  <th></th><th></th>
                  {canEdit && <th></th>}
                </tr>
              </thead>
              <tbody>
                {(order.items || []).map(item => {
                  const key = `${order.orderNo}|${item.sku}`;
                  const ed = edits[key] || {};
                  const cellcom = ed.cellcomQty ?? item.cellcomQty ?? 0;
                  const left = (item.totalQty || 0) - cellcom;
                  return (
                    <tr key={key}>
                      <td><input type="checkbox" checked={selected.has(key)} onChange={() => toggleSelect(key)} /></td>
                      <td>{item.sku}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10 }}>{item.barcode}</div>}</td>
                      <td>{item.description}</td>
                      <td className="text-end fw-bold">{fmtQty(item.totalQty)}</td>
                      <td className="text-end" style={{ background: 'rgba(200,162,44,0.03)' }}>
                        {canEdit ? <input className="inline-edit" type="number" value={cellcom}
                          onChange={e => updateEdit(order.orderNo, item.sku, 'cellcomQty', +e.target.value)} style={{width:60}} />
                          : fmtQty(cellcom)}
                      </td>
                      <td style={{ background: 'rgba(200,162,44,0.03)' }}>
                        {canEdit ? (
                          <select className="inline-edit" value={ed.transportMode ?? item.transportMode ?? ''}
                            onChange={e => updateEdit(order.orderNo, item.sku, 'transportMode', e.target.value)}>
                            <option value="">{'\u2014'}</option>
                            <option value="AIR">AIR</option>
                            <option value="SEA">OCEAN</option>
                          </select>
                        ) : (item.transportMode || '\u2014')}
                      </td>
                      <td className="text-center" style={{ background: 'rgba(200,162,44,0.03)' }}>
                        {canEdit ? <input type="checkbox" checked={ed.sticker ?? item.sticker ?? false}
                          onChange={e => updateEdit(order.orderNo, item.sku, 'sticker', e.target.checked)} />
                          : (item.sticker ? '\u2713' : '')}
                      </td>
                      <td className={`text-end fw-bold ${left === 0 ? 'text-success' : left < 0 ? 'text-danger' : 'text-warning'}`}>
                        {fmtQty(left)}
                      </td>
                      <td>
                        {canEdit ? <input className="inline-edit" placeholder="Notes..." value={ed.notes ?? item.notes ?? ''}
                          onChange={e => updateEdit(order.orderNo, item.sku, 'notes', e.target.value)} style={{width:100}} />
                          : (item.notes || '')}
                      </td>
                      {canEdit && (
                        <td>
                          {edits[key] && <button className="btn btn-gold btn-sm" onClick={() => saveAllocation(order.orderNo, item.sku, item)}>Save</button>}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="sub-tabs">
        {TABS.map(tb => (
          <button key={tb} className={`sub-tab${tab === tb ? ' active' : ''}`} onClick={() => setTab(tb)}>
            {tb === 'allocations' ? t('warehouse.stockSummary')
              : tb === 'newOrder' ? t('warehouse.newOrder')
              : tb === 'futureOrder' ? t('warehouse.futureOrder')
              : tb === 'cellcomUpload' ? t('warehouse.uploadOrder')
              : tb === 'cellcomOrders' ? t('warehouse.cellcomOrders')
              : tb === 'completed' ? t('warehouse.completed')
              : tb === 'allOrders' ? 'All Orders'
              : tb === 'extWarehouse' ? `📦 ${t('logistics.externalWarehouse')}`
              : tb === 'pullOrders' ? `🚚 ${t('logistics.pullOrders')}`
              : tb === 'qrSerials' ? `🔲 QR Serials`
              : t('warehouse.archive')}
          </button>
        ))}
      </div>

      {/* ═══ External Warehouse Tab (moved from Logistics 2026-05-04) ═══ */}
      {tab === 'extWarehouse' && <ExternalWarehouseView canEdit={canEdit} t={t} hasRole={hasRole} setLogTab={setTab} />}

      {/* ═══ Pull Orders Tab (moved from Logistics) ═══ */}
      {tab === 'pullOrders' && <PullOrdersView canEdit={canEdit} t={t} hasRole={hasRole} />}

      {/* ═══ QR Serials Tab (duplicated from Logistics) ═══ */}
      {tab === 'qrSerials' && <QRSerialsView canEdit={canEdit} t={t} />}

      {/* ═══ Allocations Tab — Traffic Light ═══ */}
      {tab === 'allocations' && (
        <>
          <div className="flex-between mb-16">
            <h2 style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, letterSpacing: 1 }}>
              {t('warehouse.stockSummary')}
            </h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {canEdit && !stockAddMode && (
                <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff', border: 'none' }} onClick={() => { setStockAddMode(true); setStockAddInputs({}); setStockAddTarget(''); }}>
                  ➕ {he ? 'הוסף להזמנה' : 'Add to Order'}
                </button>
              )}
              {stockAddMode && (
                <>
                  <select className="input input-sm" value={stockAddTarget} onChange={e => setStockAddTarget(e.target.value)} style={{ width: 140 }}>
                    <option value="">{he ? 'בחר הזמנה...' : 'Select order...'}</option>
                    {cellcomOrders.filter(o => o.status !== 'cancelled' && o.status !== 'completed').map(o => (
                      <option key={o.order_number} value={o.order_number}>#{o.order_number} ({o.item_count} items)</option>
                    ))}
                  </select>
                  {stockAddTarget && Object.values(stockAddInputs).some(v => (v.fromST || 0) + (v.fromShager || 0) > 0) && (
                    <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff', border: 'none' }} onClick={async () => {
                      const items = Object.entries(stockAddInputs).filter(([, v]) => (v.fromST || 0) + (v.fromShager || 0) > 0).map(([sku, v]) => {
                        const s = stockSummary.find(x => x.sku === sku) || {};
                        // Inputs hold combined (internal + external) qty per owner. Draw internal first, then external.
                        const intST = s.stAvailable || 0, intShager = s.shagerAvailable || 0;
                        const stEntered = v.fromST || 0, shEntered = v.fromShager || 0;
                        return {
                          sku, description: s.description || '', barcode: s.barcode || '',
                          fromST: Math.min(stEntered, intST),
                          fromExtSt: Math.max(0, stEntered - intST),
                          fromShager: Math.min(shEntered, intShager),
                          fromExtShager: Math.max(0, shEntered - intShager),
                        };
                      });
                      if (!items.length) return;
                      try {
                        await api.post('/warehouse/add-to-order', { orderNumber: parseInt(stockAddTarget), items, transportMode: 'SEA' });
                        setStockAddMode(false); setStockAddInputs({}); setStockAddTarget('');
                        fetchTab(true);
                      } catch (err) { notifyError(err); }
                    }}>
                      ➕ {he ? 'הוסף' : 'Add'} ({Object.values(stockAddInputs).filter(v => (v.fromST || 0) + (v.fromShager || 0) > 0).length})
                    </button>
                  )}
                  <button className="btn btn-sm btn-outline" onClick={() => { setStockAddMode(false); setStockAddInputs({}); }}>{he ? 'ביטול' : 'Cancel'}</button>
                </>
              )}
              <button className="btn btn-gold" onClick={exportAllAllocations}>Export All</button>
            </div>
          </div>

          {/* Stock Filter Cubes */}
          {stockSummary.length > 0 && (() => {
            // Per-owner totals INCLUDE external-warehouse stock (S&T + ext S&T, Shager + ext Shager),
            // so the cards reconcile with the Total (which already includes external).
            const totalST = stockSummary.reduce((s, i) => s + (i.stAvailable || 0) + (i.externalSt || 0), 0);
            const totalShager = stockSummary.reduce((s, i) => s + (i.shagerAvailable || 0) + (i.externalShager || 0), 0);
            const totalAll = stockSummary.reduce((s, i) => s + i.totalAvailable, 0);
            const skusST = stockSummary.filter(i => ((i.stAvailable || 0) + (i.externalSt || 0)) > 0).length;
            const skusShager = stockSummary.filter(i => ((i.shagerAvailable || 0) + (i.externalShager || 0)) > 0).length;
            return (
            <div className="summary-cards" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 20 }}>
              <div className={`summary-card clickable${stockFilter === 'st' ? '' : ''}`}
                onClick={() => setStockFilter(f => f === 'st' ? 'all' : 'st')}
                style={{ cursor: 'pointer', borderLeft: `4px solid #1565c0`, background: stockFilter === 'st' ? '#e3f2fd' : '#fff', transition: 'background 0.15s' }}>
                <div className="label" style={{ color: '#1565c0', fontWeight: 700, fontSize: 12 }}>{t('warehouse.stStock')}</div>
                <div className="value" style={{ color: '#1565c0' }}>{fmtQty(totalST)}</div>
                <div className="sub">{skusST} {t('warehouse.skus')}</div>
              </div>
              <div className={`summary-card clickable`}
                onClick={() => setStockFilter(f => f === 'shager' ? 'all' : 'shager')}
                style={{ cursor: 'pointer', borderLeft: `4px solid #4caf50`, background: stockFilter === 'shager' ? '#e8f5e9' : '#fff', transition: 'background 0.15s' }}>
                <div className="label" style={{ color: '#4caf50', fontWeight: 700, fontSize: 12 }}>{t('warehouse.shagerStock')}</div>
                <div className="value" style={{ color: '#4caf50' }}>{fmtQty(totalShager)}</div>
                <div className="sub">{skusShager} {t('warehouse.skus')}</div>
              </div>
              <div className={`summary-card clickable`}
                onClick={() => setStockFilter('all')}
                style={{ cursor: 'pointer', borderLeft: `4px solid #333`, background: stockFilter === 'all' ? '#f5f5f5' : '#fff', transition: 'background 0.15s' }}>
                <div className="label" style={{ fontWeight: 700, fontSize: 12 }}>{t('warehouse.totalAvailable')}</div>
                <div className="value">{fmtQty(totalAll)}</div>
                <div className="sub">{stockSummary.filter(i => i.totalAvailable > 0).length} {t('warehouse.skus')}</div>
              </div>
            </div>
            );
          })()}

          {/* Stock Summary Table */}
          {stockSummary.length > 0 && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: stockFilter === 'st' ? '#1565c0' : stockFilter === 'shager' ? '#4caf50' : 'var(--text-dim)' }}>
                  {stockFilter === 'st' ? t('warehouse.stStock') : stockFilter === 'shager' ? t('warehouse.shagerStock') : t('warehouse.stockSummary')}
                </h3>
                <span className="text-muted" style={{ fontSize: 13 }}>
                  {stockFilter !== 'all' && <button className="btn btn-sm btn-outline" style={{ marginInlineEnd: 8, fontSize: 11 }} onClick={() => setStockFilter('all')}>✕ {t('orders.all')}</button>}
                  {stockSummary.length} {t('warehouse.products')}
                </span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => setStockSort(p => ({ field: 'sku', dir: p.field === 'sku' && p.dir === 'asc' ? 'desc' : 'asc' }))}>{t('common.sku')} {stockSort.field === 'sku' ? (stockSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => setStockSort(p => ({ field: 'description', dir: p.field === 'description' && p.dir === 'asc' ? 'desc' : 'asc' }))}>{t('common.product')} {stockSort.field === 'description' ? (stockSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                    <th className="text-end" style={{ color: '#1565c0' }}>S&T</th>
                    <th className="text-end" style={{ color: '#4caf50' }}>Shager</th>
                    <th className="text-end fw-bold" style={{ cursor: 'pointer' }} onClick={() => setStockSort(p => ({ field: 'totalAvailable', dir: p.field === 'totalAvailable' && p.dir === 'desc' ? 'asc' : 'desc' }))}>{t('common.total')} {stockSort.field === 'totalAvailable' ? (stockSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                    {stockAddMode && <th className="text-end" style={{ width: 70, background: '#f5f3ff', borderBottom: '2px solid #7c3aed', fontSize: 11 }}>{he ? 'מ-S&T' : 'From S&T'}</th>}
                    {stockAddMode && <th className="text-end" style={{ width: 70, background: '#f5f3ff', borderBottom: '2px solid #7c3aed', fontSize: 11 }}>{he ? 'משאגר' : 'From Shager'}</th>}
                    {!stockAddMode && <th className="text-end" style={{ background: '#fff3e0', borderBottom: '2px solid #ff9800', fontSize: 11 }}>Min S&T</th>}
                    {!stockAddMode && <th className="text-end" style={{ background: '#fff3e0', borderBottom: '2px solid #ff9800', fontSize: 11 }}>Min Shager</th>}
                    {!stockAddMode && <th className="text-end" style={{ background: '#fff3e0', borderBottom: '2px solid #ff9800', fontSize: 11, fontWeight: 700 }}>Min Keep</th>}
                  </tr></thead>
                  <tbody>
                    {[...stockSummary]
                    .filter(item => stockFilter === 'st' ? ((item.stAvailable || 0) + (item.externalSt || 0)) > 0 : stockFilter === 'shager' ? ((item.shagerAvailable || 0) + (item.externalShager || 0)) > 0 : true)
                    .sort((a, b) => {
                      const f = stockSort.field;
                      let va = a[f], vb = b[f];
                      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
                      if (va < vb) return stockSort.dir === 'asc' ? -1 : 1;
                      if (va > vb) return stockSort.dir === 'asc' ? 1 : -1;
                      return 0;
                    }).map(item => (
                      <tr key={item.sku}>
                        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10, fontFamily: 'Arial,sans-serif' }}>{item.barcode}</div>}</td>
                        <td>{item.description}</td>
                        <td className="text-end" style={{ color: ((item.stAvailable || 0) + (item.externalSt || 0)) > 0 ? '#1565c0' : '#999' }}>{fmtQty((item.stAvailable || 0) + (item.externalSt || 0))}</td>
                        <td className="text-end" style={{ color: ((item.shagerAvailable || 0) + (item.externalShager || 0)) > 0 ? '#4caf50' : '#999' }}>{fmtQty((item.shagerAvailable || 0) + (item.externalShager || 0))}</td>
                        <td className="text-end fw-bold">{fmtQty(item.totalAvailable)}</td>
                        {stockAddMode ? (<>
                          <td style={{ background: '#faf5ff' }}>
                            {((item.stAvailable || 0) + (item.externalSt || 0)) > 0 && <input className="inline-edit" type="number" min="0" max={(item.stAvailable || 0) + (item.externalSt || 0)}
                              defaultValue="" placeholder="0"
                              onBlur={async e => { let v = parseInt(e.target.value) || 0; if (v > 0) v = await validateMasterCarton(v, item.sku); e.target.value = v || ''; setStockAddInputs(prev => ({ ...prev, [item.sku]: { ...(prev[item.sku] || {}), fromST: v } })); }}
                              style={{ width: 60, textAlign: 'right', fontSize: 12 }} />}
                          </td>
                          <td style={{ background: '#faf5ff' }}>
                            {((item.shagerAvailable || 0) + (item.externalShager || 0)) > 0 && <input className="inline-edit" type="number" min="0" max={(item.shagerAvailable || 0) + (item.externalShager || 0)}
                              defaultValue="" placeholder="0"
                              onBlur={async e => { let v = parseInt(e.target.value) || 0; if (v > 0) v = await validateMasterCarton(v, item.sku); e.target.value = v || ''; setStockAddInputs(prev => ({ ...prev, [item.sku]: { ...(prev[item.sku] || {}), fromShager: v } })); }}
                              style={{ width: 60, textAlign: 'right', fontSize: 12 }} />}
                          </td>
                        </>) : (<>
                          <td className="text-end" style={{ background: '#fffbf0' }}>
                            {canEdit ? <input className="inline-edit" type="number" min="0" style={{width:55, textAlign:'right', fontSize:12}}
                              key={`ms-${item.sku}-${item.minStockST}`} defaultValue={item.minStockST || ''}  placeholder="0"
                              onBlur={e => { const v = parseFloat(e.target.value) || 0; if (v !== (item.minStockST || 0)) api.put('/warehouse/min-stock', { sku: item.sku, minStockST: v }).then(() => fetchTab(true)); }}
                            /> : fmtQty(item.minStockST || 0)}
                          </td>
                          <td className="text-end" style={{ background: '#fffbf0' }}>
                            {canEdit ? <input className="inline-edit" type="number" min="0" style={{width:55, textAlign:'right', fontSize:12}}
                              key={`msh-${item.sku}-${item.minStockShager}`} defaultValue={item.minStockShager || ''} placeholder="0"
                              onBlur={e => { const v = parseFloat(e.target.value) || 0; if (v !== (item.minStockShager || 0)) api.put('/warehouse/min-stock', { sku: item.sku, minStockShager: v }).then(() => fetchTab(true)); }}
                            /> : fmtQty(item.minStockShager || 0)}
                          </td>
                          <td className="text-end fw-bold" style={{ background: '#fffbf0', color: '#e65100' }}>{fmtQty((item.minStockST || 0) + (item.minStockShager || 0))}</td>
                        </>)}
                      </tr>
                    ))}
                    {(() => {
                      const filtered = stockSummary.filter(item => stockFilter === 'st' ? ((item.stAvailable || 0) + (item.externalSt || 0)) > 0 : stockFilter === 'shager' ? ((item.shagerAvailable || 0) + (item.externalShager || 0)) > 0 : true);
                      return (
                      <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                        <td colSpan={2}>{t('common.total')}</td>
                        <td className="text-end" style={{ color: '#1565c0' }}>{fmtQty(filtered.reduce((s, i) => s + (i.stAvailable || 0) + (i.externalSt || 0), 0))}</td>
                        <td className="text-end" style={{ color: '#4caf50' }}>{fmtQty(filtered.reduce((s, i) => s + (i.shagerAvailable || 0) + (i.externalShager || 0), 0))}</td>
                        <td className="text-end">{fmtQty(filtered.reduce((s, i) => s + i.totalAvailable, 0))}</td>
                        <td className="text-end" style={{ background: '#fffbf0' }}>{fmtQty(filtered.reduce((s, i) => s + (i.minStockST || 0), 0))}</td>
                        <td className="text-end" style={{ background: '#fffbf0' }}>{fmtQty(filtered.reduce((s, i) => s + (i.minStockShager || 0), 0))}</td>
                        <td className="text-end" style={{ background: '#fffbf0', color: '#e65100' }}>{fmtQty(filtered.reduce((s, i) => s + (i.minStockST || 0) + (i.minStockShager || 0), 0))}</td>
                      </tr>);
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {zones.map(zone => {
            const orders = ordersByZone[zone] || [];
            if (orders.length === 0) return null;
            const zc = ZONE_COLORS[zone];
            const totalUnits = orders.reduce((s, o) => s + (o.items || []).reduce((s2, i) => s2 + (i.totalQty || 0), 0), 0);
            return (
              <div key={zone} style={{ marginBottom: 24 }}>
                <div style={{
                  background: zc.bg, borderLeft: `4px solid ${zc.border}`,
                  padding: '10px 16px', borderRadius: 6, marginBottom: 8,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <span style={{ fontWeight: 700, color: zc.text, fontSize: 14, letterSpacing: 0.5 }}>
                    {zc.label}
                  </span>
                  <span style={{ color: zc.text, fontWeight: 600, fontSize: 13 }}>
                    {orders.length} orders · {fmtQty(totalUnits)} units
                  </span>
                </div>
                {orders.map(renderOrderTable)}
              </div>
            );
          })}

          {data.length === 0 && <div className="empty-state">{t('common.noData')}</div>}
        </>
      )}

      {/* ═══ New Order Tab — Immediate from Stock ═══ */}
      {tab === 'newOrder' && (() => {
        // Sellable per owner = internal stock (after min-stock reserve) + external-warehouse stock.
        // S&T column = internal S&T + external S&T; Shager = internal + external. This matches
        // the warehouse-stock view's total. Allocation draws internal first, then auto-pulls
        // external pallets (the split happens in createNewOrder below).
        const sellableStock = stockSummary.map(item => {
          const intST = Math.max(0, (item.stAvailable || 0) - (item.minStockST || 0));
          const intShager = Math.max(0, (item.shagerAvailable || 0) - (item.minStockShager || 0));
          const extSt = item.externalSt || 0;
          const extShager = item.externalShager || 0;
          return {
            ...item,
            _intST: intST, _intShager: intShager, _extSt: extSt, _extShager: extShager,
            stAvailable: intST + extSt,
            shagerAvailable: intShager + extShager,
            totalAvailable: intST + extSt + intShager + extShager,
          };
        });
        const filtered = sellableStock.filter(i => i.totalAvailable > 0 && (!orderSearch ||
          i.description.toLowerCase().includes(orderSearch.toLowerCase()) ||
          i.sku.toLowerCase().includes(orderSearch.toLowerCase())
        ));
        // Live stock calculation
        const liveStock = filtered.map(item => {
          const inp = orderInputs[item.sku] || {};
          const fromST = parseFloat(inp.fromST) || 0;
          const fromShager = parseFloat(inp.fromShager) || 0;
          return { ...item, fromST, fromShager, liveST: item.stAvailable - fromST, liveShager: item.shagerAvailable - fromShager, liveTotal: item.totalAvailable - fromST - fromShager };
        });
        const orderItems = liveStock.filter(i => i.fromST > 0 || i.fromShager > 0);
        const totalFromST = orderItems.reduce((s, i) => s + i.fromST, 0);
        const totalFromShager = orderItems.reduce((s, i) => s + i.fromShager, 0);
        const totalUnits = totalFromST + totalFromShager;

        const createNewOrder = async () => {
          if (orderItems.length === 0) return notifyMessage('No items selected', 'לא נבחרו פריטים');
          if (!(await confirm({ body: he ? `ליצור הזמנה עם ${orderItems.length} מוצרים, ${fmtQty(totalUnits)} יחידות?` : `Create order with ${orderItems.length} products, ${fmtQty(totalUnits)} units?` }))) return;
          try {
            const items = orderItems.map(i => {
              // Draw internal stock first, then external (per owner). Backend deducts
              // fromST/fromShager from internal and auto-pulls fromExtSt/fromExtShager from pallets.
              const fromST = Math.min(i.fromST, i._intST || 0);
              const fromExtSt = Math.max(0, i.fromST - (i._intST || 0));
              const fromShager = Math.min(i.fromShager, i._intShager || 0);
              const fromExtShager = Math.max(0, i.fromShager - (i._intShager || 0));
              return {
                sku: i.sku, description: i.description,
                fromST, fromShager, fromExtSt, fromExtShager,
                barcode: i.barcode, origin: i.origin,
              };
            });
            await api.post('/warehouse/create-stock-order', { items, transportMode: createTransport });
            setOrderInputs({});
            setTab('cellcomOrders');
            fetchTab(true);
          } catch (err) { notifyError(err); }
        };

        return (
          <>
            <div className="flex-between mb-16">
              <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>{t('warehouse.newOrder')}</h2>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
              <input className="input" placeholder={'🔍 ' + t('warehouse.searchProducts')} value={orderSearch} onChange={e => setOrderSearch(e.target.value)} style={{ maxWidth: 350 }} />
              <button className="btn btn-gold btn-sm" onClick={() => {
                const allInputs = {};
                for (const item of filtered) {
                  allInputs[item.sku] = { fromST: item.stAvailable, fromShager: item.shagerAvailable };
                }
                setOrderInputs(allInputs);
              }}>All Stock</button>
              <button className="btn btn-outline btn-sm" onClick={() => setOrderInputs({})}>Clear</button>
            </div>

            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>{t('common.product')}</th><th>{t('common.sku')}</th>
                  <th className="text-end" style={{ color: '#1565c0' }}>S&T</th>
                  <th className="text-end" style={{ color: '#4caf50' }}>Shager</th>
                  <th className="text-end fw-bold">{t('common.total')}</th>
                  <th className="text-end" style={{ background: 'rgba(21,101,192,0.06)' }}>From S&T</th>
                  <th className="text-end" style={{ background: 'rgba(76,175,80,0.06)' }}>From Shager</th>
                  <th className="text-end fw-bold">Order Total</th>
                </tr></thead>
                <tbody>
                  {liveStock.map(item => (
                    <tr key={item.sku} style={(item.fromST > 0 || item.fromShager > 0) ? { background: 'rgba(200,162,44,0.05)' } : {}}>
                      <td>{item.description}</td>
                      <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku}</td>
                      <td className="text-end" style={{ color: item.liveST > 0 ? '#1565c0' : '#ccc', fontWeight: item.fromST > 0 ? 700 : 400 }}>{fmtQty(item.liveST)}</td>
                      <td className="text-end" style={{ color: item.liveShager > 0 ? '#4caf50' : '#ccc', fontWeight: item.fromShager > 0 ? 700 : 400 }}>{fmtQty(item.liveShager)}</td>
                      <td className="text-end fw-bold">{fmtQty(item.liveTotal)}</td>
                      <td className="text-end" style={{ background: 'rgba(21,101,192,0.06)' }}>
                        <input className="inline-edit" type="number" min="0" max={item.stAvailable} style={{ width: 70, textAlign: 'right' }}
                          value={item.fromST || ''} placeholder="0"
                          onChange={e => setOrderInputs(prev => ({ ...prev, [item.sku]: { ...(prev[item.sku] || {}), fromST: e.target.value } }))}
                        />
                      </td>
                      <td className="text-end" style={{ background: 'rgba(76,175,80,0.06)' }}>
                        <input className="inline-edit" type="number" min="0" max={item.shagerAvailable} style={{ width: 70, textAlign: 'right' }}
                          value={item.fromShager || ''} placeholder="0"
                          onChange={e => setOrderInputs(prev => ({ ...prev, [item.sku]: { ...(prev[item.sku] || {}), fromShager: e.target.value } }))}
                        />
                      </td>
                      <td className="text-end fw-bold" style={{ color: (item.fromST + item.fromShager) > 0 ? 'var(--gold)' : '#ccc' }}>
                        {(item.fromST + item.fromShager) > 0 ? fmtQty(item.fromST + item.fromShager) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Live Order Summary */}
            {orderItems.length > 0 && (
              <div className="card" style={{ marginTop: 24 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-dim)' }}>
                    Order Summary
                  </h3>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Product</th><th>SKU</th><th className="text-end">From S&T</th><th className="text-end">From Shager</th><th className="text-end fw-bold">Total</th><th className="text-end">CBM</th><th className="text-end">Weight</th></tr></thead>
                    <tbody>
                      {orderItems.map(i => {
                        const qty = i.fromST + i.fromShager;
                        const itemCBM = qty * (i.cbmPerUnit || 0);
                        const itemKg = qty * (i.weightKg || 0);
                        return (
                          <tr key={i.sku}>
                            <td>{i.description}</td>
                            <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{i.sku}</td>
                            <td className="text-end" style={{ color: '#1565c0' }}>{i.fromST > 0 ? fmtQty(i.fromST) : '—'}</td>
                            <td className="text-end" style={{ color: '#4caf50' }}>{i.fromShager > 0 ? fmtQty(i.fromShager) : '—'}</td>
                            <td className="text-end fw-bold">{fmtQty(qty)}</td>
                            <td className="text-end" style={{ color: '#6c757d' }}>{itemCBM > 0 ? itemCBM.toFixed(2) : '—'}</td>
                            <td className="text-end" style={{ color: '#6c757d' }}>{itemKg > 0 ? fmtWeight(itemKg) : '—'}</td>
                          </tr>
                        );
                      })}
                      {(() => {
                        const totalCBM = orderItems.reduce((s, i) => s + (i.fromST + i.fromShager) * (i.cbmPerUnit || 0), 0);
                        const totalWeightKg = orderItems.reduce((s, i) => s + (i.fromST + i.fromShager) * (i.weightKg || 0), 0);
                        const totalPallets = Math.ceil(totalCBM / PALLET_CBM);
                        const totalContainers = Math.ceil(totalPallets / PALLETS_PER_40HC);
                        return (<>
                          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                            <td colSpan={2}>{t('common.total')}</td>
                            <td className="text-end" style={{ color: '#1565c0' }}>{fmtQty(totalFromST)}</td>
                            <td className="text-end" style={{ color: '#4caf50' }}>{fmtQty(totalFromShager)}</td>
                            <td className="text-end">{fmtQty(totalUnits)} units</td>
                            <td className="text-end" style={{ fontWeight: 700 }}>{totalCBM.toFixed(2)}</td>
                            <td className="text-end" style={{ fontWeight: 700 }}>{totalWeightKg > 0 ? fmtWeight(totalWeightKg) : '—'}</td>
                          </tr>
                          {totalCBM > 0 && (<>
                            <tr style={{ background: '#f0f7ff' }}>
                              <td colSpan={3} style={{ fontWeight: 600, color: '#1565c0' }}>📦 Shipping</td>
                              <td className="text-end" style={{ fontWeight: 700, color: '#1565c0' }}>{totalCBM.toFixed(2)} CBM</td>
                              <td className="text-end" style={{ fontWeight: 700, color: '#1565c0' }}>{totalPallets} pallets</td>
                              <td className="text-muted" style={{ fontSize: 11 }}>1.0×1.2m, h=2.2m</td>
                              <td className="text-end" style={{ fontWeight: 700, color: '#1565c0' }}>{totalWeightKg > 0 ? fmtWeight(totalWeightKg) : ''}</td>
                            </tr>
                            {(() => {
                              const exactC = Math.ceil(totalPallets / 20);
                              const bufferC = totalContainers;
                              const renderRow = (label, color, bg, perC, numC) => (
                                <tr style={{ background: bg }}>
                                  <td colSpan={3} style={{ fontWeight: 600, color }}>🚛 {label}</td>
                                  <td className="text-end" style={{ fontWeight: 700, color }}>{numC} × 40HC</td>
                                  <td colSpan={3} style={{ fontSize: 12, color }}>
                                    {Array.from({ length: numC }, (_, c) => {
                                      const p = c < numC - 1 ? perC : totalPallets - (numC - 1) * perC;
                                      const cbm = p * PALLET_CBM;
                                      const wkg = totalWeightKg > 0 ? (totalWeightKg * p / totalPallets) : 0;
                                      return (
                                        <span key={c} style={{ display: 'inline-block', marginRight: 12, padding: '2px 8px', background: color === '#2e7d32' ? '#c8e6c9' : '#bbdefb', borderRadius: 4, marginBottom: 2 }}>
                                          <strong>#{c + 1}</strong>: {p}p · {cbm.toFixed(1)} CBM{wkg > 0 ? ` · ${fmtWeight(wkg)}` : ''} <span style={{ fontSize: 10, color: '#546e7a' }}>({(p / 20 * 100).toFixed(0)}%)</span>
                                        </span>
                                      );
                                    })}
                                  </td>
                                </tr>
                              );
                              const CONTAINER_CBM = 67.25;
                              const looseC = Math.ceil(totalCBM / CONTAINER_CBM);
                              const fillPct = Math.round((totalCBM / (looseC * CONTAINER_CBM)) * 100);
                              return (<>
                                {renderRow('With Pallets (20p)', '#2e7d32', '#e8f5e9', 20, exactC)}
                                <tr style={{ background: '#e3f2fd' }}>
                                  <td colSpan={3} style={{ fontWeight: 600, color: '#0d47a1' }}>🚛 Without Pallets</td>
                                  <td className="text-end" style={{ fontWeight: 700, color: '#0d47a1' }}>{looseC} × 40HC</td>
                                  <td colSpan={3} style={{ fontSize: 12, color: '#0d47a1' }}>
                                    {totalCBM.toFixed(1)} / {(looseC * CONTAINER_CBM).toFixed(1)} CBM · {fillPct}% full
                                  </td>
                                </tr>
                              </>);
                            })()}
                          </>)}
                        </>);
                      })()}
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <select className="input" value={createTransport} onChange={e => setCreateTransport(e.target.value)} style={{ width: 80 }}>
                    <option value="SEA">OCEAN</option>
                    <option value="AIR">AIR</option>
                  </select>
                  <button className="btn btn-outline" onClick={() => {
                    const rows = orderItems.map(i => ({
                      'SKU': i.sku,
                      'Product': i.description,
                      'From S&T': i.fromST || 0,
                      'From Shager': i.fromShager || 0,
                      'Total': (i.fromST || 0) + (i.fromShager || 0),
                      'Transport': createTransport,
                      'Barcode': i.barcode || '',
                    }));
                    const ws = XLSX.utils.json_to_sheet(rows);
                    ws['!cols'] = [{wch:12},{wch:35},{wch:10},{wch:12},{wch:8},{wch:10},{wch:16}];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'New Order');
                    XLSX.writeFile(wb, 'new_order.xlsx');
                  }}>{t('warehouse.exportExcelBtn')}</button>
                  <button className="btn btn-gold" onClick={createNewOrder}>Create New Order</button>
                  {cellcomOrders.length > 0 && (
                    <>
                      <span className="text-muted" style={{ fontSize: 12 }}>or</span>
                      <select className="input" value={addToOrderNum || ''} onChange={e => setAddToOrderNum(e.target.value)} style={{ width: 180 }}>
                        <option value="">Add to existing...</option>
                        {cellcomOrders.map(o => (
                          <option key={o.order_number} value={o.order_number}>#{o.order_number} ({o.item_count} items)</option>
                        ))}
                      </select>
                      {addToOrderNum && (
                        <button className="btn" style={{ background: '#1565c0', color: '#fff' }} onClick={async () => {
                          if (!orderItems.length) return notifyMessage('No items selected', 'לא נבחרו פריטים');
                          if (!(await confirm({ body: he ? `להוסיף ${orderItems.length} מוצרים להזמנה #${addToOrderNum}?` : `Add ${orderItems.length} products to order #${addToOrderNum}?` }))) return;
                          try {
                            await api.post('/warehouse/add-to-order', {
                              orderNumber: parseInt(addToOrderNum),
                              items: orderItems.map(i => ({ sku: i.sku, description: i.description, fromST: i.fromST, fromShager: i.fromShager, barcode: i.barcode })),
                              transportMode: createTransport,
                            });
                            setOrderInputs({});
                            setAddToOrderNum('');
                            setTab('cellcomOrders');
                            fetchTab(true);
                          } catch (err) { notifyError(err); }
                        }}>Add to #{addToOrderNum}</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* ═══ Future Order Tab — From Incoming ═══ */}
      {tab === 'futureOrder' && (() => {
        const now = new Date(); now.setHours(0,0,0,0);
        const filtered = incomingOrders.filter(o => !futureSearch || (o.items || []).some(i =>
          i.description.toLowerCase().includes(futureSearch.toLowerCase()) ||
          i.sku.toLowerCase().includes(futureSearch.toLowerCase())
        ));

        // Collect all selected future items
        const futureItems = [];
        for (const order of incomingOrders) {
          for (const item of (order.items || [])) {
            const key = `${order.orderNo}|${item.sku}`;
            const qty = parseFloat(futureInputs[key]) || 0;
            if (qty > 0) futureItems.push({ ...item, orderNo: order.orderNo, qty, arrivalDate: order.arrivalDate, zone: order.zone });
          }
        }
        // Stock reservations for future
        const futureStockItems = [];
        for (const item of stockSummary) {
          const inp = futureStockInputs[item.sku] || {};
          const fromST = parseFloat(inp.fromST) || 0;
          const fromShager = parseFloat(inp.fromShager) || 0;
          if (fromST > 0 || fromShager > 0) futureStockItems.push({ ...item, fromST, fromShager });
        }
        const totalIncoming = futureItems.reduce((s, i) => s + i.qty, 0);
        const totalStock = futureStockItems.reduce((s, i) => s + i.fromST + i.fromShager, 0);

        const createFutureOrder = async () => {
          if (futureItems.length === 0 && futureStockItems.length === 0) return notifyMessage('No items selected', 'לא נבחרו פריטים');
          if (!(await confirm({ body: he ? `ליצור הזמנה עתידית עם ${futureItems.length + futureStockItems.length} מוצרים?` : `Create future order with ${futureItems.length + futureStockItems.length} products?` }))) return;
          try {
            await api.post('/warehouse/create-future-order', {
              incomingItems: futureItems.map(i => ({ orderNo: i.orderNo, sku: i.sku, description: i.description, quantity: i.qty, barcode: i.displaySku })),
              stockItems: futureStockItems.map(i => ({ sku: i.sku, description: i.description, fromST: i.fromST, fromShager: i.fromShager, barcode: i.barcode })),
              transportMode: createTransport,
            });
            setFutureInputs({});
            setFutureStockInputs({});
            setTab('cellcomOrders');
            fetchTab(true);
          } catch (err) { notifyError(err); }
        };

        return (
          <>
            <div className="flex-between mb-16">
              <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>{t('warehouse.futureOrder')}</h2>
            </div>
            <input className="input mb-16" placeholder={'🔍 ' + t('warehouse.searchProducts')} value={futureSearch} onChange={e => setFutureSearch(e.target.value)} style={{ maxWidth: 350 }} />

            {filtered.length === 0 ? <div className="empty-state">No incoming orders</div> : filtered.map(order => {
              const zc = ZONE_COLORS[order.zone] || ZONE_COLORS.red;
              const arrival = order.arrivalDate ? new Date(order.arrivalDate) : null;
              const daysLeft = arrival ? Math.max(0, Math.ceil((arrival - now) / (1000*60*60*24))) : null;
              const foKey = `fo-${order.orderNo}`;
              const isOpen = expanded.has(foKey);
              const totalAllocated = (order.items || []).reduce((s, i) => s + (i.futureAllocated || 0), 0);
              return (
                <div key={order.orderNo} className="order-group" style={{ marginBottom: 8 }}>
                  <div className="order-group-header" onClick={() => toggle(foKey)}
                    style={{ background: zc.bg, borderLeft: `4px solid ${zc.border}`, padding: '8px 16px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                    <span style={{ fontWeight: 700, color: zc.text }}>{order.orderNo}</span>
                    {order.origin && <span className={`badge badge-${order.origin.toLowerCase()}`}>{order.origin}</span>}
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      ETA: {arrival ? `${fmtDate(arrival)} (${daysLeft} ${t('common.days')})` : '—'}
                    </span>
                    {totalAllocated > 0 && <span style={{ fontSize: 11, color: '#e65100', fontWeight: 600 }}>📦 {fmtQty(totalAllocated)} allocated</span>}
                    <div style={{ flex: 1 }} />
                    <span className="fw-bold" style={{ color: zc.text }}>
                      {fmtQty((order.items || []).reduce((s, i) => s + (parseFloat(i.totalQty) || 0), 0))} {t('common.units')}
                    </span>
                  </div>
                  {isOpen && <div className="table-wrap">
                    <table>
                      <thead><tr>
                        <th>Product</th><th>SKU</th><th className="text-end">Incoming</th>
                        <th className="text-end" style={{ color: '#e65100' }}>Allocated</th>
                        <th className="text-end" style={{ color: '#2e7d32' }}>Available</th>
                        <th>ETA</th>
                        <th className="text-end" style={{ background: 'rgba(200,162,44,0.06)' }}>Qty</th>
                      </tr></thead>
                      <tbody>
                        {(order.items || []).filter(i => !futureSearch ||
                          i.description.toLowerCase().includes(futureSearch.toLowerCase()) ||
                          i.sku.toLowerCase().includes(futureSearch.toLowerCase())
                        ).map(item => {
                          const key = `${order.orderNo}|${item.sku}`;
                          return (
                            <tr key={key} style={futureInputs[key] > 0 ? { background: 'rgba(200,162,44,0.05)' } : {}}>
                              <td>{item.description}</td>
                              <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku}</td>
                              <td className="text-end fw-bold">{fmtQty(item.totalQty)}</td>
                              <td className="text-end" style={{ color: item.futureAllocated > 0 ? '#e65100' : '#999', fontWeight: item.futureAllocated > 0 ? 700 : 400 }}>
                                {item.futureAllocated > 0 ? fmtQty(item.futureAllocated) : '—'}
                              </td>
                              <td className="text-end fw-bold" style={{ color: item.availableQty > 0 ? '#2e7d32' : '#c62828' }}>
                                {fmtQty(item.availableQty)}
                              </td>
                              <td className="text-muted">{arrival ? `${fmtDate(arrival)} (${daysLeft}d)` : '—'}</td>
                              <td className="text-end" style={{ background: 'rgba(200,162,44,0.06)' }}>
                                <input className="inline-edit" type="number" min="0" max={item.availableQty} style={{ width: 70, textAlign: 'right' }}
                                  value={futureInputs[key] || ''} placeholder="0"
                                  onChange={e => setFutureInputs(prev => ({ ...prev, [key]: e.target.value }))}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>}
                </div>
              );
            })}

            {/* Reserve from existing stock */}
            {stockSummary.filter(i => i.totalAvailable > 0).length > 0 && (
              <div className="card" style={{ marginTop: 16 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-dim)' }}>{t('warehouse.fromST')} / {t('warehouse.fromShager')}</h3>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>{t('common.product')}</th><th>{t('common.sku')}</th>
                      <th className="text-end" style={{ color: '#1565c0' }}>S&T</th>
                      <th className="text-end" style={{ color: '#4caf50' }}>Shager</th>
                      <th className="text-end" style={{ background: 'rgba(21,101,192,0.06)' }}>From S&T</th>
                      <th className="text-end" style={{ background: 'rgba(76,175,80,0.06)' }}>From Shager</th>
                    </tr></thead>
                    <tbody>
                      {stockSummary.filter(i => i.totalAvailable > 0).map(item => (
                        <tr key={item.sku}>
                          <td>{item.description}</td>
                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{item.sku}</td>
                          <td className="text-end" style={{ color: '#1565c0' }}>{fmtQty(item.stAvailable)}</td>
                          <td className="text-end" style={{ color: '#4caf50' }}>{fmtQty(item.shagerAvailable)}</td>
                          <td className="text-end" style={{ background: 'rgba(21,101,192,0.06)' }}>
                            <input className="inline-edit" type="number" min="0" style={{ width: 60, textAlign: 'right' }}
                              value={(futureStockInputs[item.sku] || {}).fromST || ''} placeholder="0"
                              onChange={e => setFutureStockInputs(prev => ({ ...prev, [item.sku]: { ...(prev[item.sku] || {}), fromST: e.target.value } }))}
                            />
                          </td>
                          <td className="text-end" style={{ background: 'rgba(76,175,80,0.06)' }}>
                            <input className="inline-edit" type="number" min="0" style={{ width: 60, textAlign: 'right' }}
                              value={(futureStockInputs[item.sku] || {}).fromShager || ''} placeholder="0"
                              onChange={e => setFutureStockInputs(prev => ({ ...prev, [item.sku]: { ...(prev[item.sku] || {}), fromShager: e.target.value } }))}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Future Order Summary */}
            {(futureItems.length > 0 || futureStockItems.length > 0) && (
              <div className="card" style={{ marginTop: 24 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-dim)' }}>
                    Future Order Summary
                  </h3>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Product</th><th>SKU</th><th>Source</th><th className="text-end">Qty</th></tr></thead>
                    <tbody>
                      {futureItems.map(i => (
                        <tr key={`${i.orderNo}-${i.sku}`}>
                          <td>{i.description}</td>
                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{i.sku}</td>
                          <td className="text-muted">{i.orderNo}</td>
                          <td className="text-end fw-bold">{fmtQty(i.qty)}</td>
                        </tr>
                      ))}
                      {futureStockItems.map(i => (
                        <tr key={`stock-${i.sku}`}>
                          <td>{i.description}</td>
                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{i.sku}</td>
                          <td className="text-muted">{i.fromST > 0 ? `S&T: ${fmtQty(i.fromST)}` : ''}{i.fromST > 0 && i.fromShager > 0 ? ' + ' : ''}{i.fromShager > 0 ? `Shager: ${fmtQty(i.fromShager)}` : ''}</td>
                          <td className="text-end fw-bold">{fmtQty(i.fromST + i.fromShager)}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                        <td colSpan={3}>{t('common.total')}</td>
                        <td className="text-end">{fmtQty(totalIncoming + totalStock)} units</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end' }}>
                  <select className="input" value={createTransport} onChange={e => setCreateTransport(e.target.value)} style={{ width: 80 }}>
                    <option value="SEA">OCEAN</option>
                    <option value="AIR">AIR</option>
                  </select>
                  <button className="btn btn-gold" onClick={createFutureOrder}>Create Future Order</button>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* ═══ Cellcom Upload Tab — Upload Excel Order ═══ */}
      {tab === 'cellcomUpload' && <CellcomPage />}

      {/* ═══ Cellcom Orders Tab — Expandable/Collapsible ═══ */}
      {tab === 'cellcomOrders' && (
        <>
          {renderChainBand()}
          <div className="flex-between" style={{ marginBottom: 16 }}>
            <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>{t('warehouse.cellcomOrders')}</h2>
            {canEdit && (
              <button className="btn" style={{ background: '#1565c0', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: 8, fontWeight: 700 }}
                onClick={async () => {
                  if (!(await confirm({ body: he ? 'לפרסם את ההזמנות הנוכחיות ללקוח?' : 'Publish current orders to customer portal?' }))) return;
                  try {
                    const res = await api.post('/portal/publish-orders');
                    alert(he ? `${res.data.published} הזמנות פורסמו ללקוח` : `${res.data.published} orders published to customer`);
                  } catch (err) { notifyError(err); }
                }}>
                📤 {he ? 'עדכן הזמנות ללקוח' : 'Update Client Orders'}
              </button>
            )}
            {canEdit && (
              <button className="btn btn-sm" style={{ background: '#16a34a', color: '#fff', border: 'none' }}
                onClick={async () => {
                  try {
                    const res = await api.post('/settings/backup');
                    alert(he ? `✅ גיבוי נשמר: ${res.data.filename}` : `✅ Backup saved: ${res.data.filename}`);
                  } catch (err) { notifyError(err); }
                }}>
                💾 {he ? 'גיבוי' : 'Backup'}
              </button>
            )}
          </div>
          {selectedOrderNos.size > 0 && (() => {
            const selOrders = cellcomOrders.filter(o => selectedOrderNos.has(o.order_number));
            const totUnits = selOrders.reduce((s, o) => s + parseFloat(o.total_units || 0), 0);
            const totValue = selOrders.reduce((s, o) => s + parseFloat(o.total_value || 0), 0);
            const totCBM = selOrders.reduce((s, o) => s + parseFloat(o.total_cbm || 0), 0);
            const totWeight = selOrders.reduce((s, o) => s + parseFloat(o.total_weight || 0), 0);
            const totPallets = Math.ceil(totCBM / PALLET_CBM);
            const totContainers = totCBM > 0 ? Math.ceil(totCBM / 67.25) : 0;
            return (
              <div style={{ display: 'flex', gap: 16, padding: '10px 16px', background: '#eff6ff', borderRadius: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, border: '1px solid #bfdbfe' }}>
                <span style={{ fontWeight: 700, color: '#1565c0' }}>{selectedOrderNos.size} {he ? 'הזמנות נבחרו' : 'orders selected'}</span>
                <span>{fmtQty(totUnits)} {he ? 'יחידות' : 'units'}</span>
                <span>{totCBM.toFixed(2)} CBM</span>
                <span>{totPallets} {he ? 'משטחים' : 'pallets'}</span>
                <span>{totContainers}×40HC</span>
                <span>{(totWeight / 1000).toFixed(2)}t</span>
                <span style={{ fontWeight: 700 }}>${totValue.toLocaleString()}</span>
                <div style={{ flex: 1 }} />
                <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }} onClick={() => setSelectedOrderNos(new Set())}>✕ {he ? 'נקה' : 'Clear'}</button>
              </div>
            );
          })()}
          {selectedOrderNos.size > 1 && (() => {
            const selOrders = cellcomOrders.filter(o => selectedOrderNos.has(o.order_number));
            const CONT_CBM = 67.25; // 40HC
            // Per order
            const perOrder = selOrders.map(o => {
              const cbm = parseFloat(o.total_cbm || 0);
              return { no: o.order_number, cbm, containers: cbm > 0 ? Math.ceil(cbm / CONT_CBM) : 0, transport: (o.shipping_method || o.transport_mode || '').toUpperCase() };
            });
            const separateContainers = perOrder.reduce((s, o) => s + o.containers, 0);
            const combinedCBM = perOrder.reduce((s, o) => s + o.cbm, 0);
            const combinedContainers = combinedCBM > 0 ? Math.ceil(combinedCBM / CONT_CBM) : 0;
            const saved = separateContainers - combinedContainers;
            return (
              <div style={{ padding: '10px 16px', background: saved > 0 ? '#f0fdf4' : '#f8f9fa', borderRadius: 8, marginBottom: 12, border: `1px solid ${saved > 0 ? '#86efac' : '#e2e8f0'}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: saved > 0 ? '#166534' : '#374151' }}>
                  🚛 {he ? 'ניתוח מכולות' : 'Container Analysis'}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, marginBottom: 8 }}>
                  {perOrder.map(o => (
                    <span key={o.no} style={{ padding: '3px 8px', background: '#fff', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                      #{o.no}: {o.cbm.toFixed(1)} CBM → {o.containers}×40HC {o.transport === 'AIR' ? '✈️' : '🚢'}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
                  <span>{he ? 'בנפרד' : 'Separate'}: <strong>{separateContainers}×40HC</strong></span>
                  <span>{he ? 'ביחד' : 'Combined'}: <strong style={{ color: '#1565c0' }}>{combinedContainers}×40HC</strong> ({combinedCBM.toFixed(1)} / {combinedContainers * CONT_CBM} CBM · {Math.round(combinedCBM / (combinedContainers * CONT_CBM) * 100)}% full)</span>
                  {saved > 0 && <span style={{ fontWeight: 700, color: '#16a34a' }}>💰 {he ? `חוסכים ${saved} מכולות!` : `Save ${saved} container${saved > 1 ? 's' : ''}!`}</span>}
                  {saved === 0 && <span className="text-muted">{he ? 'אין חיסכון באיחוד' : 'No savings from combining'}</span>}
                </div>
              </div>
            );
          })()}
          {selectedOrderNos.size > 0 && (() => {
            const items = [...selectedOrderNos].flatMap(no => cellcomItemsCache[no] || []);
            const summary = aggregateByModel(items, 'effectiveQty');
            if (summary.length === 0) return null;
            const grandQty = summary.reduce((s, m) => s + m.totalQty, 0);
            const grandVal = summary.reduce((s, m) => s + m.totalValue, 0);
            return (
              <div style={{ background: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', border: '2px solid #1d4ed8', borderRadius: 8, padding: 12, marginBottom: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 14 }}>📊 {he ? `סיכום ${selectedOrderNos.size} הזמנות לפי מודל + צבע` : `Summary of ${selectedOrderNos.size} orders by model + color`}</strong>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 12, color: '#1e3a8a' }}>{grandQty.toLocaleString()} {he ? 'יח׳' : 'units'} · {fmt(grandVal)}</span>
                  <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }}
                    onClick={() => {
                      const rows = [];
                      for (const m of summary) {
                        rows.push({ Model: m.model, Color: '', Qty: m.totalQty, Value: m.totalValue, Lines: m.lineCount });
                        for (const c of m.colors) rows.push({ Model: '', Color: c.color, Qty: c.qty, Value: c.value, Lines: c.lineCount });
                        rows.push({});
                      }
                      const ws = XLSX.utils.json_to_sheet(rows);
                      ws['!cols'] = [{ wch: 22 }, { wch: 22 }, { wch: 10 }, { wch: 14 }, { wch: 8 }];
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, 'By Model + Color');
                      XLSX.writeFile(wb, `Cellcom_summary_by_model_${selectedOrderNos.size}orders.xlsx`);
                    }}>📥 Excel</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                  {summary.map(m => (
                    <div key={m.model} style={{ background: '#fff', padding: '8px 12px', borderRadius: 6, border: '1px solid #93c5fd' }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{m.model}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#1d4ed8' }}>
                        {m.totalQty.toLocaleString()} <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>{he ? 'יח׳' : 'units'}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{m.lineCount} {he ? 'שורות' : 'lines'} · {fmt(m.totalValue)}</div>
                      {m.colors.length > 0 && (
                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {m.colors.map(c => (
                            <div key={c.color} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, alignItems: 'baseline' }}>
                              <span style={{ color: '#475569' }}>{c.color}</span>
                              <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.qty.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {cellcomOrders.filter(o => o.status !== 'completed' && o.status !== 'archived').length === 0 ? <div className="empty-state">{t('warehouse.noOrders')}</div> : (
            cellcomOrders.filter(o => o.status !== 'completed' && o.status !== 'archived').map(o => {
              const isOpen = expanded.has(`co-${o.order_number}`);
              const totalUnits = parseFloat(o.total_units || 0);
              const totalValue = parseFloat(o.total_value || 0);
              const statusColor = o.status === 'completed' ? '#2e7d32' : o.status === 'ready' ? '#4caf50' : o.status === 'shipped' ? '#1565c0' : '#ff9800';
              const statusBg = o.status === 'completed' ? '#e8f5e9' : o.status === 'ready' ? '#e8f5e9' : o.status === 'shipped' ? '#e3f2fd' : '#fff3e0';
              const statusText = o.status === 'completed' ? '✓ Completed' : o.status === 'picked' ? '✓ Picked' : o.status === 'ready' ? '✓ Ready' : o.status === 'shipped' ? '✓ Shipped' : '⏳ Pending';
              const pickedBg = o.status === 'picked' ? '#e0f2f1' : statusBg;
              const pickedColor = o.status === 'picked' ? '#00796b' : statusColor;
              return (
                <div key={o.id} className="order-group" style={{ marginBottom: 8 }}>
                  <div className="order-group-header" onClick={() => toggle(`co-${o.order_number}`)}>
                    <input type="checkbox" checked={selectedOrderNos.has(o.order_number)} onClick={e => e.stopPropagation()}
                      onChange={async () => {
                        setSelectedOrderNos(prev => { const s = new Set(prev); s.has(o.order_number) ? s.delete(o.order_number) : s.add(o.order_number); return s; });
                        if (!cellcomItemsCache[o.order_number]) {
                          try {
                            const r = await api.get(`/warehouse/cellcom-orders/${o.order_number}`);
                            const items = (r.data?.items || []).map(it => ({
                              description: it.description, sku: it.sku,
                              originalQty: parseFloat(it.quantity) || 0,
                              customerQty: null,
                              customerPrice: parseFloat(it.selling_price) || 0,
                            }));
                            setCellcomItemsCache(prev => ({ ...prev, [o.order_number]: items }));
                          } catch {}
                        }
                      }} style={{ marginInlineEnd: 4 }} />
                    <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                    <span className="order-no">#{o.order_number}</span>
                    <span className="text-muted">{new Date(o.created_at).toLocaleDateString()}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: pickedBg, color: pickedColor }}>{statusText}</span>
                    {(o.shipping_method || o.transport_mode) && (
                      <span className={`badge ${(o.shipping_method || o.transport_mode || '').toUpperCase() === 'AIR' ? 'badge-air' : 'badge-sea'}`}>
                        {(o.shipping_method || o.transport_mode || '').toUpperCase() === 'AIR' ? 'Air' : 'Ocean'}
                      </span>
                    )}
                    {o.sent_to_customer && !o.approval_status && <span style={{ fontSize: 11, color: '#1565c0', fontWeight: 600 }}>📤 Sent</span>}
                    {o.approval_status === 'pending' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#fff3e0', color: '#e65100' }}>{'⏳ ' + t('warehouse.awaitingApproval')}</span>}
                    {o.approval_status === 'approved' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#e8f5e9', color: '#2e7d32' }}>{'✓ ' + t('warehouse.approved')}</span>}
                    {o.approval_status === 'rejected' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#fbe9e7', color: '#c62828' }}>{'✗ ' + t('warehouse.rejected')}</span>}
                    {o.estimated_arrival && <span className="text-muted" style={{ fontSize: 11 }}>ETA: {fmtDate(o.estimated_arrival)}</span>}
                    <span onClick={e => e.stopPropagation()}>
                      <select style={{ fontSize: 10, padding: '2px 4px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer' }}
                        value={o.customer_status || 'pending'}
                        onChange={async (e) => {
                          try { await api.put(`/portal/customer-status/${o.order_number}`, { status: e.target.value }); fetchTab(true); }
                          catch (err) { notifyError(new Error('Unknown')); }
                        }}>
                        <option value="pending">📋 Pending</option>
                        <option value="confirmed">✓ Confirmed</option>
                        <option value="collecting">📦 Collecting</option>
                        <option value="delivered_ff">🏭 Delivered to FF</option>
                        <option value="shipped_customer">🚢 Shipped to Customer</option>
                        <option value="delivered">✅ Delivered</option>
                      </select>
                    </span>
                    <div style={{ flex: 1 }} />
                    <span className="text-muted">{o.item_count} SKUs · {fmtQty(totalUnits)} units · {parseFloat(o.total_cbm || 0).toFixed(2)} CBM{parseFloat(o.total_weight || 0) > 0 ? ` · ${fmtWeight(parseFloat(o.total_weight))}` : ''}{parseFloat(o.total_cbm || 0) > 0 ? ` · ${Math.ceil(parseFloat(o.total_cbm) / 67.25)}×40HC` : ''}</span>
                    <span className="fw-bold" style={{ marginInlineStart: 12 }}>{fmt(totalValue)}</span>
                  </div>
                  {isOpen && (
                    <div className="order-group-body">
                      {/* Shipping info + edit */}
                      {canEdit && (
                        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: '#fafafa', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          {editingShipping?.orderNumber === o.order_number ? (
                            <>
                              <select className="input" value={editingShipping.shippingMethod} onChange={e => setEditingShipping(prev => ({ ...prev, shippingMethod: e.target.value }))} style={{ width: 100 }}>
                                <option value="">--</option>
                                <option value="AIR">Air</option>
                                <option value="SEA">Ocean</option>
                              </select>
                              <label style={{ fontSize: 11, color: '#6b7280' }}>ETA:</label>
                              <input className="input" type="date" value={editingShipping.estimatedArrival} onChange={e => setEditingShipping(prev => ({ ...prev, estimatedArrival: e.target.value }))} style={{ width: 140 }} />
                              <label style={{ fontSize: 11, color: '#6b7280' }}>Ship Out:</label>
                              <input className="input" type="date" value={editingShipping.shippingDate || ''} onChange={e => setEditingShipping(prev => ({ ...prev, shippingDate: e.target.value }))} style={{ width: 140 }} />
                              <label style={{ fontSize: 11, color: '#6b7280' }}>Arrival:</label>
                              <input className="input" type="date" value={editingShipping.arrivalDate || ''} onChange={e => setEditingShipping(prev => ({ ...prev, arrivalDate: e.target.value }))} style={{ width: 140 }} />
                              <button className="btn btn-sm btn-gold" onClick={() => handleUpdateShipping(o.order_number)}>Save</button>
                              <button className="btn btn-sm" onClick={() => setEditingShipping(null)}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-sm" onClick={() => setEditingShipping({ orderNumber: o.order_number, estimatedArrival: o.estimated_arrival ? o.estimated_arrival.split('T')[0] : '', shippingMethod: o.shipping_method || '', shippingDate: o.shipping_date ? o.shipping_date.split('T')[0] : '', arrivalDate: o.arrival_date ? o.arrival_date.split('T')[0] : '' })}>
                                {t('warehouse.updateShipping')}
                              </button>
                              {o.status === 'shipped' && (
                                <button className="btn btn-sm btn-danger" onClick={() => handleUnship(o.order_number)}>
                                  {t('warehouse.cancelShipped')}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <CellcomOrderDetail orderNumber={o.order_number} onItemsChanged={() => fetchTab(true)} allOrders={cellcomOrders.map(co => co.order_number)}
                        actionButtons={<>
                          {canEdit && (
                            <button className="btn btn-sm" style={{ background: '#c62828', color: '#fff' }}
                              onClick={async () => {
                                if (!(await confirm({ body: he ? `לבטל הזמנה #${o.order_number}? פריטים יוחזרו למלאי.` : `Cancel order #${o.order_number}? Items will return to stock.`, danger: true }))) return;
                                try { await api.delete(`/warehouse/cellcom-orders/${o.order_number}`); fetchTab(true); }
                                catch (err) { notifyError(err); }
                              }}>{t('warehouse.cancelOrder')}</button>
                          )}
                          {canEdit && o.status !== 'picked' && o.status !== 'shipped' && (
                            <button className="btn btn-sm" style={{ background: '#00796b', color: '#fff' }}
                              onClick={async () => {
                                try { await api.put(`/warehouse/cellcom-orders/${o.order_number}/status`, { status: 'picked' }); fetchTab(true); }
                                catch (err) { notifyError(err); }
                              }}>{'✓ ' + t('warehouse.markPicked')}</button>
                          )}
                          {!o.approval_status && canEdit && (
                            <button className="btn btn-sm" style={{ background: '#ff9800', color: '#fff' }}
                              onClick={async () => {
                                if (!(await confirm({ body: he ? `לשלוח הזמנה #${o.order_number} ללקוח לאישור?` : `Send order #${o.order_number} to customer for approval?` }))) return;
                                try {
                                  await api.put(`/warehouse/cellcom-orders/${o.order_number}/send-approval`);
                                  fetchTab(true);
                                  const res2 = await api.get('/warehouse/cellcom-orders');
                                  setCellcomOrders(res2.data);
                                } catch (err) { notifyError(err); }
                              }}>{t('warehouse.sendForApproval')}</button>
                          )}
                          {!o.sent_to_customer && canEdit && (
                            <button className="btn btn-sm" style={{ background: '#1565c0', color: '#fff' }}
                              onClick={() => sendToCustomer(o.order_number)}>{t('warehouse.sendToCustomer')}</button>
                          )}
                          {canEdit && o.status !== 'shipped' && o.status !== 'completed' && (
                            <button className="btn btn-sm" style={{ background: '#0d47a1', color: '#fff' }}
                              onClick={async () => {
                                try { await api.put(`/warehouse/cellcom-orders/${o.order_number}/status`, { status: 'shipped' }); fetchTab(true); }
                                catch (err) { notifyError(err); }
                              }}>🚢 Ship</button>
                          )}
                          {canEdit && (
                            <button className="btn btn-sm" style={{ background: '#2e7d32', color: '#fff' }}
                              onClick={async () => {
                                try { await api.put(`/warehouse/cellcom-orders/${o.order_number}/status`, { status: 'completed' }); fetchTab(true); }
                                catch (err) { notifyError(err); }
                              }}>✓ Complete</button>
                          )}
                          <button className="btn btn-outline btn-sm" onClick={async () => {
                            try {
                              const res = await api.get(`/warehouse/cellcom-orders/${o.order_number}`);
                              const items = res.data.items || [];
                              const rows = items.map(item => ({
                                'Order #': o.order_number, 'Source': item.source_order_no, 'SKU': item.sku,
                                'Barcode': item.barcode || '', 'Product': item.description, 'Qty': parseFloat(item.quantity),
                                'Transport': item.transport_mode || '', 'Zone': item.zone || '', 'Notes': '',
                              }));
                              const ws = XLSX.utils.json_to_sheet(rows);
                              ws['!cols'] = [{wch:10},{wch:14},{wch:10},{wch:16},{wch:35},{wch:8},{wch:10},{wch:8},{wch:20}];
                              const wb = XLSX.utils.book_new();
                              XLSX.utils.book_append_sheet(wb, ws, 'Pick Order');
                              XLSX.writeFile(wb, `cellcom_order_${o.order_number}.xlsx`);
                            } catch {}
                          }}>{t('warehouse.exportExcelBtn')}</button>
                        </>}
                      />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </>
      )}

      {/* ═══ Completed / Archived ═══ */}
      {tab === 'completed' && (() => {
        const completedOrders = cellcomOrders.filter(o => o.status === 'completed');
        if (completedOrders.length === 0) return null;
        return (
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>Completed Cellcom Orders</h3>
            {completedOrders.map(o => (
              <div key={o.order_number} className="order-group" style={{ marginBottom: 8 }}>
                <div className="order-group-header" onClick={() => toggle(`cc-${o.order_number}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px' }}>
                  <span className={`arrow${expanded.has(`cc-${o.order_number}`) ? ' open' : ''}`}>&#9654;</span>
                  <span className="order-no">#{o.order_number}</span>
                  <span className="badge" style={{ background: '#e8f5e9', color: '#2e7d32' }}>✓ Completed</span>
                  <span className="text-muted" style={{ fontSize: 12 }}>{o.created_at ? fmtDate(o.created_at) : ''}</span>
                  <div style={{ flex: 1 }} />
                  <span className="text-muted" style={{ fontSize: 13 }}>{fmtQty(parseFloat(o.total_units || 0))} units</span>
                  <span className="fw-bold">{fmt(parseFloat(o.total_value || 0))}</span>
                </div>
                {expanded.has(`cc-${o.order_number}`) && (
                  <div className="order-group-body">
                    <CellcomOrderDetail orderNumber={o.order_number} onItemsChanged={() => fetchTab(true)} allOrders={[]} actionButtons={
                      canEdit && (
                        <button className="btn btn-sm btn-outline" style={{ color: '#e65100', borderColor: '#e65100' }}
                          onClick={async () => {
                            if (!(await confirm({ body: he ? `לפתוח מחדש הזמנה #${o.order_number}?` : `Reopen order #${o.order_number}?` }))) return;
                            try { await api.put(`/warehouse/cellcom-orders/${o.order_number}/status`, { status: 'shipped' }); fetchTab(true); }
                            catch (err) { notifyError(err); }
                          }}>↩ Reopen</button>
                      )
                    } />
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {(tab === 'completed' || tab === 'archived') && (
        data.length === 0 && cellcomOrders.filter(o => o.status === 'completed').length === 0 ? <div className="empty-state">{t('common.noData')}</div> : data.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Order</th><th>SKU</th><th>Description</th><th className="text-end">Qty</th><th>Date</th>{canEdit && <th></th>}</tr>
              </thead>
              <tbody>
                {data.map((item, i) => (
                  <tr key={i}>
                    <td className="text-gold">{item.order_no}</td>
                    <td>{item.sku || item.displaySku}</td>
                    <td>{item.description}</td>
                    <td className="text-end">{fmtQty(item.quantity)}</td>
                    <td className="text-muted">{item.handled_at ? new Date(item.handled_at).toLocaleDateString() : '--'}</td>
                    {canEdit && (
                      <td style={{ display: 'flex', gap: 4 }}>
                        {tab === 'completed' && (
                          <button className="btn btn-outline btn-sm" onClick={() => handleArchive(item.order_no, item.sku, true)}>Archive</button>
                        )}
                        {tab === 'archived' && (
                          <button className="btn btn-outline btn-sm" onClick={() => handleArchive(item.order_no, item.sku, false)}>Unarchive</button>
                        )}
                        <button className="btn btn-sm" style={{ background: '#c62828', color: '#fff' }} onClick={async () => {
                          if (!(await confirm({ body: he ? `להחזיר ${item.sku} (${item.description}) להקצאות המחסן?` : `Return ${item.sku} (${item.description}) to warehouse allocations?` }))) return;
                          try {
                            await api.put('/warehouse/cancel-completed', { orderNo: item.order_no, sku: item.sku });
                            fetchTab(true);
                          } catch (err) { notifyError(err); }
                        }}>Cancel</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null
      )}

      {tab === 'allOrders' && renderChainBand()}
      {tab === 'allOrders' && (() => {
        const all = cellcomOrders.filter(o => o.status !== 'cancelled');
        if (all.length === 0) return <div className="empty-state">{t('warehouse.noOrders')}</div>;
        return all.map(o => {
          const isOpen = expanded.has(`all-${o.order_number}`);
          const totalUnits = parseFloat(o.total_units || 0);
          const totalValue = parseFloat(o.total_value || 0);
          const statusColor = o.status === 'completed' ? '#2e7d32' : o.status === 'ready' ? '#4caf50' : o.status === 'shipped' ? '#1565c0' : '#ff9800';
          const statusText = o.status === 'completed' ? '✓ Completed' : o.status === 'picked' ? '✓ Picked' : o.status === 'ready' ? '✓ Ready' : o.status === 'shipped' ? '✓ Shipped' : '⏳ Pending';
          return (
            <div key={o.order_number} className="order-group" style={{ marginBottom: 8 }}>
              <div className="order-group-header" onClick={() => toggle(`all-${o.order_number}`)}
                style={{ display: 'grid', gridTemplateColumns: '24px 80px 100px 80px 1fr auto auto', alignItems: 'center', gap: 10 }}>
                <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                <span className="order-no">#{o.order_number}</span>
                <span className="badge" style={{ background: statusColor + '20', color: statusColor, fontSize: 11, fontWeight: 700 }}>{statusText}</span>
                <span className="text-muted" style={{ fontSize: 12 }}>{o.created_at ? fmtDate(o.created_at) : ''}</span>
                <span className="text-muted" style={{ fontSize: 13 }}>{fmtQty(totalUnits)} units · {parseFloat(o.total_cbm || 0).toFixed(2)} CBM</span>
                <span className="fw-bold">{fmt(totalValue)}</span>
              </div>
              {isOpen && (
                <div className="order-group-body">
                  <CellcomOrderDetail orderNumber={o.order_number} onItemsChanged={() => fetchTab(true)} allOrders={cellcomOrders.map(co => co.order_number)} actionButtons={null} />
                </div>
              )}
            </div>
          );
        });
      })()}
    </div>
  );
}
